/**
 * Elite Batch 11 Part 3 — Workflow Templates 988–1007
 * FAQ threads, content calendars, repermission, onboarding, video ads, founder posts,
 * frequency preferences, full-funnel campaigns, gamification, geo-targeting, suppression,
 * Google Ads, CRM syncs, review responders, customer match, and hashtag generation.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function sendgridNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlContent: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.sendgrid.com/v3/mail/send",
        headers: [
          { key: "Authorization", value: "Bearer {sendgrid_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "personalizations", value: `[{"to":[{"email":"${to}"}]}]` },
          {
            key: "from",
            value: '{"email":"{from_email}","name":"{company_name}"}',
          },
          { key: "subject", value: subject },
          {
            key: "content",
            value: `[{"type":"text/html","value":"${htmlContent}"}]`,
          },
        ],
        variables: [
          { name: "statusCode", customName: "sg_status", type: "number" },
        ],
      },
    },
    x,
  );
}
function hubspotCreateNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyPayload: string,
  outputVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: bodyPayload }],
        variables: outputVars,
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 11 PART 3 — TEMPLATES 988–1007
// =============================================================================

export const ELITE_BATCH11_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 988. FAQ THREAD CREATOR (7 nodes)
  // ===================================================================

  "FAQ Thread Creator": {
    summary:
      "Aggregates FAQ sources on schedule, deduplicates and ranks questions with code, composes structured FAQ threads via AI, checks brand accuracy with a condition, routes through approval, publishes to multiple platforms, and logs engagement to Sheets.",
    nodes: [
      trigger("Schedule: FAQ source aggregation", "schedule", {
        schedule: "0 7 * * 1 (every Monday at 7 AM)",
        howItStarts:
          "A scheduled job aggregates raw FAQ data from support tickets, community forums, and chatbot logs to identify the most frequently asked questions for thread creation",
        setup:
          "Connect support platform APIs (Zendesk, Intercom, Freshdesk) for ticket data. Configure community API for forum questions. Set minimum question frequency threshold (e.g., 3+ occurrences per week).",
        inputFields: [],
      }),

      // 2. Question dedup + ranking (runCode)
      n("2", "runCode", "Deduplicate and rank questions by frequency", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Deduplicate questions by semantic similarity and rank by frequency
const raw = JSON.parse(vars.webhook_payload || "{}");
const tickets = raw.tickets || [];
const forum = raw.forum_posts || [];
const chatbot = raw.chatbot_queries || [];
const all = [...tickets.map(t => t.subject || t.question), ...forum.map(f => f.title || f.question), ...chatbot.map(c => c.query)].filter(Boolean);
const freq = {};
all.forEach(q => {
  const normalized = q.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
  const key = normalized.substring(0, 80);
  freq[key] = freq[key] || { question: q, count: 0 };
  freq[key].count++;
});
const ranked = Object.values(freq).sort((a, b) => b.count - a.count).slice(0, 20);
return {
  ranked_questions_json: JSON.stringify(ranked),
  total_unique: Object.keys(freq).length,
  top_question: ranked.length > 0 ? ranked[0].question : "",
  top_question_count: ranked.length > 0 ? ranked[0].count : 0,
  qualified_count: ranked.filter(r => r.count >= 3).length
};`,
          outputVariables: [
            { name: "ranked_questions_json", type: "string" },
            { name: "total_unique", type: "number" },
            { name: "top_question", type: "string" },
            { name: "top_question_count", type: "number" },
            { name: "qualified_count", type: "number" },
          ],
        },
      }),

      // 3. AI FAQ thread composer
      aiNode(
        "3",
        "AI: Compose structured FAQ thread",
        520,
        "You are a technical content writer who creates clear, comprehensive FAQ threads. Structure each answer with a brief summary, detailed explanation, and relevant links. Use a friendly but professional tone. Return JSON.",
        "Create a structured FAQ thread from these ranked questions: {ranked_questions_json}. Top question asked {top_question_count} times: {top_question}. For each, write faq_question (cleaned up), faq_answer (2-3 paragraphs), short_answer (1 sentence). Return thread_title (string), thread_intro (string), faqs (array of {faq_question, faq_answer, short_answer}), total_faqs (number), thread_html (complete HTML formatted thread).",
        [
          { field: "thread_title", as: "faq_thread_title", type: "string" },
          { field: "thread_intro", as: "faq_thread_intro", type: "string" },
          { field: "total_faqs", as: "total_faqs_composed", type: "number" },
          { field: "thread_html", as: "faq_thread_html", type: "string" },
          { field: "faqs", as: "faqs_json", type: "string" },
        ],
      ),

      // 4. Brand + accuracy review condition
      n("4", "condition", "Does thread pass brand and accuracy review?", 760, {
        conditionData: [
          {
            variable: "total_faqs_composed",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
          {
            variable: "qualified_count",
            value: "2",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Content team: Approve FAQ thread", 1000, {
        approvalGateDetails: {
          approvers: ["{content_team_email}"],
          message:
            "New FAQ thread ready for review: {faq_thread_title}. Contains {total_faqs_composed} FAQs sourced from {total_unique} unique questions. Top question: {top_question} (asked {top_question_count} times). Please review for brand voice compliance and factual accuracy.",
          timeout: 48,
        },
      }),

      // 6. Multi-platform publisher
      n("6", "hitApi", "Publish FAQ thread to platforms", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{community_domain}/api/posts.json",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Api-Username", value: "system" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{faq_thread_title}" },
            { key: "raw", value: "{faq_thread_html}" },
            { key: "category", value: "{faq_category_id}" },
          ],
          variables: [
            { name: "id", customName: "published_thread_id", type: "string" },
            {
              name: "topic_slug",
              customName: "published_thread_slug",
              type: "string",
            },
          ],
        },
      }),

      // 7. Engagement monitor (Sheets)
      sheetsNode(
        "7",
        "Log FAQ thread to engagement tracker",
        1480,
        "FAQ_Threads",
        `[["${new Date().toISOString()}","{faq_thread_title}","{total_faqs_composed}","{total_unique}","{top_question}","{published_thread_id}","published"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes review -> approval
      e("4", "7", "output_2"), // fails review -> log as rejected
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 989. FACEBOOK CONTENT CALENDAR POST (7 nodes)
  // ===================================================================

  "Facebook Content Calendar Post": {
    summary:
      "Triggers from a content calendar, selects the day's theme, generates AI-composed Facebook posts, creates images with DALL-E, routes through compliance approval, publishes to Facebook, and tracks performance in Sheets.",
    nodes: [
      trigger("Schedule: Content calendar post", "schedule", {
        schedule: "0 8 * * 1-5 (weekdays at 8 AM)",
        howItStarts:
          "A scheduled job pulls the current day's content theme from the editorial calendar and initiates Facebook post creation",
        setup:
          "Set up a Google Sheet or Airtable as content calendar with columns: date, theme, audience, tone, key_message. Configure Facebook Page access token with publish_pages permission. Provide DALL-E API key for image generation.",
        inputFields: [],
      }),

      // 2. Theme selector
      n("2", "hitApi", "Fetch today's content theme from calendar", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{content_calendar_sheet_id}/values/Calendar!A:F?majorDimension=ROWS",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "calendar_rows_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI Facebook post composer
      aiNode(
        "3",
        "AI: Compose Facebook post with theme",
        520,
        "You are a social media expert specializing in Facebook content. Create engaging, platform-native posts that drive comments and shares. Use conversational tone, emojis sparingly, and strong CTAs. Return JSON.",
        "Create a Facebook post based on today's content calendar entry. Calendar data: {calendar_rows_json}. Today's date: ${new Date().toISOString().split('T')[0]}. Find today's row and compose: post_text (max 500 chars, engaging, with CTA), image_prompt (detailed DALL-E prompt for a supporting image), hashtags (3-5 relevant), best_time_to_post (HH:MM format), theme_used (the theme from calendar), audience_targeted (from calendar).",
        [
          { field: "post_text", as: "fb_post_text", type: "string" },
          { field: "image_prompt", as: "dalle_image_prompt", type: "string" },
          { field: "hashtags", as: "fb_hashtags", type: "string" },
          {
            field: "best_time_to_post",
            as: "optimal_post_time",
            type: "string",
          },
          { field: "theme_used", as: "content_theme", type: "string" },
          { field: "audience_targeted", as: "target_audience", type: "string" },
        ],
      ),

      // 4. Image/media generator (DALL-E)
      n("4", "hitApi", "DALL-E: Generate post image", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            { key: "prompt", value: "{dalle_image_prompt}" },
            { key: "n", value: "1" },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "standard" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "generated_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 5. Compliance approval gate
      n("5", "approvalGate", "Compliance: Approve Facebook post", 1000, {
        approvalGateDetails: {
          approvers: ["{compliance_email}"],
          message:
            "Facebook post ready for compliance review.\\n\\nTheme: {content_theme}\\nAudience: {target_audience}\\nPost text: {fb_post_text}\\nHashtags: {fb_hashtags}\\nImage: {generated_image_url}\\n\\nPlease verify brand guidelines, legal compliance, and image appropriateness.",
          timeout: 24,
        },
      }),

      // 6. Facebook publisher
      n("6", "hitApi", "Publish post to Facebook Page", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{fb_page_id}/photos",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "url", value: "{generated_image_url}" },
            { key: "caption", value: "{fb_post_text} {fb_hashtags}" },
            { key: "access_token", value: "{fb_page_access_token}" },
          ],
          variables: [
            { name: "id", customName: "fb_post_id", type: "string" },
            { name: "post_id", customName: "fb_full_post_id", type: "string" },
          ],
        },
      }),

      // 7. Performance tracker (Sheets)
      sheetsNode(
        "7",
        "Log post to performance tracker",
        1480,
        "FB_Performance",
        `[["${new Date().toISOString()}","{content_theme}","{target_audience}","{fb_post_text}","{fb_hashtags}","{generated_image_url}","{fb_post_id}","published","{optimal_post_time}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 990. FINAL REPERMISSION FLOW (6 nodes)
  // ===================================================================

  "Final Repermission Flow": {
    summary:
      "Scans for inactive subscribers on schedule, generates a personalized AI last-chance email, dispatches the repermission email, handles confirmation responses, processes expirations and removals with code, and logs compliance audit results to Sheets.",
    nodes: [
      trigger("Schedule: Inactive subscriber scan", "schedule", {
        schedule: "0 6 * * 1 (every Monday at 6 AM)",
        howItStarts:
          "A scheduled job scans the email list for subscribers who have not opened or clicked any email in the past 90 days and initiates a final repermission flow before list removal",
        setup:
          "Configure SendGrid or your ESP to provide engagement data. Set inactivity threshold (default: 90 days no opens/clicks). Ensure unsubscribe and preference center links are configured. Set up compliance audit trail in Sheets.",
        inputFields: [],
      }),

      // 2. AI last-chance email
      aiNode(
        "2",
        "AI: Generate personalized last-chance email",
        280,
        "You are an email retention specialist writing final repermission emails. The tone should be warm but direct — make it clear this is the last email they will receive unless they confirm. Include a clear CTA button and explain what they will miss. Return JSON.",
        "Generate a last-chance repermission email for inactive subscribers. Company: {company_name}. Write subject_line (compelling, urgency without clickbait), preview_text (40 chars), email_html (complete HTML with confirm button linking to {repermission_url}, explain value they will lose, warm goodbye if they choose not to confirm), plain_text_version (text fallback).",
        [
          { field: "subject_line", as: "repermission_subject", type: "string" },
          { field: "preview_text", as: "repermission_preview", type: "string" },
          { field: "email_html", as: "repermission_html", type: "string" },
          {
            field: "plain_text_version",
            as: "repermission_plain",
            type: "string",
          },
        ],
      ),

      // 3. Repermission email dispatch
      sendgridNode(
        "3",
        "SendGrid: Dispatch repermission email",
        520,
        "{subscriber_email}",
        "{repermission_subject}",
        "{repermission_html}",
      ),

      // 4. Confirmation handler
      n("4", "delayAction", "Wait for confirmation response", 760, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 14,
          delayUnit: "days",
          description:
            "Wait 14 days for subscriber to click the confirm button in the repermission email before processing expirations",
        },
      }),

      // 5. Expiry + removal processor (runCode)
      n("5", "runCode", "Process expirations and remove unconfirmed", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process repermission results — remove unconfirmed subscribers
const confirmed = vars.sg_status === 200 ? "sent" : "failed";
const daysSinceSend = 14;
const hasConfirmed = vars.repermission_confirmed === "true";
const action = hasConfirmed ? "retained" : "removed";
const removalReason = hasConfirmed ? "N/A" : "No repermission confirmation after " + daysSinceSend + " days";
const complianceNote = "GDPR/CAN-SPAM compliant removal. Repermission email sent on " + vars.send_date + ". Confirmation window: " + daysSinceSend + " days.";
return {
  subscriber_action: action,
  removal_reason: removalReason,
  compliance_note: complianceNote,
  send_status: confirmed,
  confirmation_received: hasConfirmed ? "yes" : "no",
  processed_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "subscriber_action", type: "string" },
            { name: "removal_reason", type: "string" },
            { name: "compliance_note", type: "string" },
            { name: "send_status", type: "string" },
            { name: "confirmation_received", type: "string" },
            { name: "processed_date", type: "string" },
          ],
        },
      }),

      // 6. Results logger + compliance audit (Sheets)
      sheetsNode(
        "6",
        "Log repermission results and compliance audit",
        1240,
        "Repermission_Audit",
        `[["${new Date().toISOString()}","{subscriber_email}","{subscriber_action}","{send_status}","{confirmation_received}","{removal_reason}","{compliance_note}","{processed_date}"]]`,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 991. FIRST-TIME APP USER WELCOME (7 nodes)
  // ===================================================================

  "First-Time App User Welcome": {
    summary:
      "Triggers on first app launch, initializes user profile in HubSpot, sends a personalized AI welcome email, monitors in-app behavior with a delay, follows up with feature discovery and power user tips emails, and alerts on engagement drop-off via Slack.",
    nodes: [
      trigger("Webhook: First app launch detected", "webhook_app", {
        source: "Segment / Mixpanel / Firebase",
        event: "app.first_launch",
        howItStarts:
          "A user launches the mobile or web app for the very first time, triggering a multi-step welcome and onboarding email sequence",
        setup:
          "Configure your analytics platform (Segment, Mixpanel, or Firebase) to fire a webhook on the first_launch event. Ensure user email and basic profile data are included in the payload. Set up HubSpot lifecycle stage mapping.",
        inputFields: [],
      }),

      // 2. User profile initializer (HubSpot)
      aiNode(
        "2",
        "AI: Extract user profile from launch event",
        280,
        "You extract new user profile data from app first-launch webhook payloads regardless of format. Return JSON with user_email, user_name, device_type (ios/android/web), app_version, signup_source, user_id. Find these fields regardless of nesting or naming convention.",
        "Extract new user data from this first app launch webhook: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "app_version", as: "app_version", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
        ],
      ),

      // 3. AI welcome email #1
      aiNode(
        "3",
        "AI: Compose welcome email #1",
        520,
        "You are an onboarding email specialist. Write a warm, concise welcome email for a first-time app user. Focus on one quick-win action they can take right now. Personalize based on device type and signup source. Return JSON.",
        "Write welcome email #1 for new user: {user_name}, device: {device_type}, signed up via: {signup_source}. Company: {company_name}. Return subject_line, email_html (warm welcome, 1 quick-win CTA, setup checklist of 3 items), quick_win_action (the primary action to drive).",
        [
          { field: "subject_line", as: "welcome1_subject", type: "string" },
          { field: "email_html", as: "welcome1_html", type: "string" },
          { field: "quick_win_action", as: "quick_win_action", type: "string" },
        ],
      ),

      // 4. In-app behavior monitor (delay)
      n("4", "delayAction", "Wait 3 days for in-app behavior", 760, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 3,
          delayUnit: "days",
          description:
            "Wait 3 days after welcome email to allow user to explore the app and build usage data before sending feature discovery email",
        },
      }),

      // 5. AI email #2 feature discovery
      aiNode(
        "5",
        "AI: Compose feature discovery email #2",
        1000,
        "You are an onboarding specialist writing a feature discovery email. Highlight 3 features the user has not yet tried based on their device type. Make each feature feel like a mini-tutorial with a clear benefit. Return JSON.",
        "Write feature discovery email #2 for user: {user_name}, device: {device_type}, quick win completed: {quick_win_action}. Company: {company_name}. Return subject_line, email_html (3 feature spotlights with benefits and how-to steps), featured_capabilities (comma-separated list of 3 features).",
        [
          { field: "subject_line", as: "discovery_subject", type: "string" },
          { field: "email_html", as: "discovery_html", type: "string" },
          {
            field: "featured_capabilities",
            as: "featured_capabilities",
            type: "string",
          },
        ],
      ),

      // 6. AI email #3 power user tips
      aiNode(
        "6",
        "AI: Compose power user tips email #3",
        1240,
        "You are an onboarding specialist writing the final onboarding email. Share 3 power-user tips and invite the user to join the community. Create a sense of accomplishment. Return JSON.",
        "Write power user tips email #3 for user: {user_name}, device: {device_type}, features introduced: {featured_capabilities}. Company: {company_name}. Return subject_line, email_html (3 power tips, community invite CTA, celebration of onboarding completion), community_cta_url (link placeholder).",
        [
          { field: "subject_line", as: "power_tips_subject", type: "string" },
          { field: "email_html", as: "power_tips_html", type: "string" },
          { field: "community_cta_url", as: "community_url", type: "string" },
        ],
      ),

      // 7. Engagement drop-off alert (Slack)
      slackNode(
        "7",
        "Slack: Alert on engagement status",
        1480,
        "Onboarding sequence completed for {user_name} ({user_email})\\n\\nDevice: {device_type} | Source: {signup_source}\\nWelcome email: sent\\nFeature discovery: sent\\nPower tips: sent\\n\\nMonitor engagement over next 7 days. If no app activity detected, trigger re-engagement flow.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 992. FOUNDER SELFIE VIDEO AD (7 nodes)
  // ===================================================================

  "Founder Selfie Video Ad": {
    summary:
      "Triggers from a campaign brief, generates an AI video script, checks script quality with a condition, renders the video via API, routes through approval, uploads to Meta ad platform, and handles errors with Slack fallback.",
    nodes: [
      trigger("Webhook: Campaign brief submitted", "webhook_app", {
        source: "Internal Dashboard / Airtable",
        event: "campaign.brief.submitted",
        howItStarts:
          "A marketing team member submits a campaign brief for a founder selfie-style video ad, triggering AI script generation and video production pipeline",
        setup:
          "Configure campaign brief form (Airtable, Outgrow Forms, or internal tool) to fire webhook with product name, target audience, key message, CTA, and brand tone. Set up video rendering API (Synthesia, HeyGen, or D-ID). Configure Meta Ads API access.",
        inputFields: [],
      }),

      // 2. AI script generation
      aiNode(
        "2",
        "AI: Generate founder video script",
        280,
        "You extract campaign brief data and generate a founder-style selfie video ad script. The script should feel authentic, conversational, and unpolished (like a real founder talking to camera). Keep it under 60 seconds. Return JSON.",
        "Extract campaign details from: {webhook_payload}. Then generate a founder selfie video script. Return product_name, target_audience, key_message, script_text (60-sec conversational script with [PAUSE] and [GESTURE] markers), hook_line (first 3 seconds), cta_line (closing CTA), estimated_duration_seconds (number), tone_notes (delivery guidance for founder).",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "key_message", as: "key_message", type: "string" },
          { field: "script_text", as: "video_script", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "cta_line", as: "cta_line", type: "string" },
          {
            field: "estimated_duration_seconds",
            as: "est_duration",
            type: "number",
          },
          { field: "tone_notes", as: "tone_notes", type: "string" },
        ],
      ),

      // 3. Script review condition
      n(
        "3",
        "condition",
        "Is script within 60-second limit and complete?",
        520,
        {
          conditionData: [
            {
              variable: "est_duration",
              value: "75",
              comparison_operator: "LESS THAN OR EQUAL TO",
              logical_operator: "AND",
            },
            {
              variable: "hook_line",
              value: "",
              comparison_operator: "NOT EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 4. Video rendering
      n("4", "hitApi", "Render founder selfie video via API", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.synthesia.io/v2/videos",
          headers: [
            { key: "Authorization", value: "{synthesia_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "input",
              value: `[{"scriptText":"{video_script}","avatar":"{founder_avatar_id}","background":"selfie_style"}]`,
            },
            { key: "title", value: "Founder Ad: {product_name}" },
            {
              key: "description",
              value: "Selfie-style ad for {target_audience}",
            },
          ],
          variables: [
            { name: "id", customName: "video_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            {
              name: "download",
              customName: "video_download_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Marketing team: Approve video ad", 1000, {
        approvalGateDetails: {
          approvers: ["{marketing_lead_email}"],
          message:
            "Founder selfie video ad ready for review.\\n\\nProduct: {product_name}\\nTarget: {target_audience}\\nHook: {hook_line}\\nCTA: {cta_line}\\nDuration: ~{est_duration}s\\nVideo: {video_download_url}\\n\\nPlease review script delivery, brand alignment, and ad policy compliance.",
          timeout: 48,
        },
      }),

      // 6. Ad platform upload (Meta)
      n("6", "hitApi", "Upload video ad to Meta Ads", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{ad_account_id}/advideos",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "file_url", value: "{video_download_url}" },
            { key: "title", value: "Founder Selfie: {product_name}" },
            { key: "description", value: "{key_message}" },
            { key: "access_token", value: "{meta_access_token}" },
          ],
          variables: [
            { name: "id", customName: "meta_video_id", type: "string" },
            { name: "success", customName: "upload_success", type: "boolean" },
          ],
        },
      }),

      // 7. Error + fallback handler (Slack)
      slackNode(
        "7",
        "Slack: Notify team of video ad status",
        1480,
        "Founder Selfie Video Ad Pipeline Complete\\n\\nProduct: {product_name}\\nAudience: {target_audience}\\nVideo ID: {video_id}\\nMeta Video ID: {meta_video_id}\\nUpload: {upload_success}\\nHook: {hook_line}\\nDuration: ~{est_duration}s\\n\\nIf upload failed, manually download from: {video_download_url} and upload to Meta Ads Manager.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // script passes -> render
      e("3", "7", "output_2"), // script too long -> alert team
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 993. FOUNDER SOCIAL POST DRAFTER (6 nodes)
  // ===================================================================

  "Founder Social Post Drafter": {
    summary:
      "Triggers from a content prompt, aggregates context from recent company activity, generates authentic AI post variants for the founder, checks brand and sensitivity with a condition, routes through founder approval, and publishes to LinkedIn.",
    nodes: [
      trigger("Webhook: Content prompt submitted", "webhook_app", {
        source: "Slack / Internal Dashboard",
        event: "content.prompt.submitted",
        howItStarts:
          "A team member submits a content prompt or topic idea for the founder to post about on social media, triggering AI draft generation",
        setup:
          "Configure prompt submission form (Slack workflow, Airtable form, or internal tool) to fire webhook with topic, context, preferred_platform, and urgency level. Set up LinkedIn API with w_member_social permission for the founder's profile.",
        inputFields: [],
      }),

      // 2. Context aggregation
      aiNode(
        "2",
        "AI: Extract prompt and aggregate context",
        280,
        "You extract content prompt details from webhook payloads and enrich with contextual information for authentic founder posts. Return JSON with topic, context_notes, preferred_platform, urgency, content_angle, recent_company_news (inferred or placeholder). Find fields regardless of format.",
        "Extract content prompt data from: {webhook_payload}. Return topic (string), context_notes (string), preferred_platform (string, default linkedin), urgency (string), content_angle (string — thought leadership, product update, industry insight, personal story, or team spotlight), audience_context (who reads the founder's posts).",
        [
          { field: "topic", as: "post_topic", type: "string" },
          { field: "context_notes", as: "context_notes", type: "string" },
          { field: "preferred_platform", as: "platform", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
          { field: "content_angle", as: "content_angle", type: "string" },
          { field: "audience_context", as: "audience_context", type: "string" },
        ],
      ),

      // 3. AI authentic post variants
      aiNode(
        "3",
        "AI: Draft authentic founder post variants",
        520,
        "You are a ghostwriter for a startup founder. Write LinkedIn posts that sound genuinely personal — use first person, share real opinions, avoid corporate jargon. Each variant should have a different hook style (question, bold statement, story opener, statistic). Return JSON.",
        "Draft 3 LinkedIn post variants for the founder. Topic: {post_topic}. Context: {context_notes}. Angle: {content_angle}. Audience: {audience_context}. Return variant_1 (question hook, max 1300 chars), variant_2 (bold statement hook, max 1300 chars), variant_3 (story hook, max 1300 chars), recommended_variant (1, 2, or 3), recommendation_reason (why this variant), hashtags (3-5), best_post_time (HH:MM), sensitivity_score (1-10, 10 = most sensitive).",
        [
          { field: "variant_1", as: "post_variant_1", type: "string" },
          { field: "variant_2", as: "post_variant_2", type: "string" },
          { field: "variant_3", as: "post_variant_3", type: "string" },
          {
            field: "recommended_variant",
            as: "recommended_variant",
            type: "string",
          },
          {
            field: "recommendation_reason",
            as: "recommendation_reason",
            type: "string",
          },
          { field: "hashtags", as: "post_hashtags", type: "string" },
          { field: "best_post_time", as: "best_post_time", type: "string" },
          {
            field: "sensitivity_score",
            as: "sensitivity_score",
            type: "number",
          },
        ],
      ),

      // 4. Brand + sensitivity review condition
      n(
        "4",
        "condition",
        "Is post within brand and sensitivity guidelines?",
        760,
        {
          conditionData: [
            {
              variable: "sensitivity_score",
              value: "7",
              comparison_operator: "LESS THAN",
              logical_operator: "AND",
            },
            {
              variable: "recommended_variant",
              value: "",
              comparison_operator: "NOT EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Founder approval gate
      n("5", "approvalGate", "Founder: Select and approve post", 1000, {
        approvalGateDetails: {
          approvers: ["{founder_email}"],
          message:
            "3 LinkedIn post variants ready for your review.\\n\\nTopic: {post_topic}\\nAngle: {content_angle}\\n\\nVariant 1 (question hook):\\n{post_variant_1}\\n\\nVariant 2 (bold statement):\\n{post_variant_2}\\n\\nVariant 3 (story hook):\\n{post_variant_3}\\n\\nRecommended: Variant {recommended_variant} — {recommendation_reason}\\nHashtags: {post_hashtags}\\nBest time: {best_post_time}",
          timeout: 24,
        },
      }),

      // 6. Post publisher (LinkedIn)
      n("6", "hitApi", "Publish post to LinkedIn", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:person:{founder_linkedin_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value: `{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{post_variant_1}\\n\\n{post_hashtags}"},"shareMediaCategory":"NONE"}}`,
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes brand check -> founder approval
      e("4", "2", "output_2"), // too sensitive -> re-draft with adjusted context
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 994. FREQUENCY-PREFERENCE CAPTURE (6 nodes)
  // ===================================================================

  "Frequency-Preference Capture": {
    summary:
      "Triggers on preference update events, generates a personalized AI preference email, dispatches it, captures the subscriber's frequency preference, syncs to HubSpot, and handles non-responses with a fallback code processor.",
    nodes: [
      trigger("Webhook: Preference capture trigger", "webhook_app", {
        source: "ESP / CRM / Preference Center",
        event: "subscriber.preference.requested",
        howItStarts:
          "A subscriber clicks a preference link, completes onboarding, or a periodic preference refresh is triggered — initiating a frequency preference capture flow",
        setup:
          "Configure your ESP or CRM to fire a webhook when preference capture is needed. Set up a hosted preference center page at {preference_center_url}. Ensure HubSpot contact properties include email_frequency_preference and last_preference_update.",
        inputFields: [],
      }),

      // 2. AI preference email
      aiNode(
        "2",
        "AI: Generate preference capture email",
        280,
        "You extract subscriber info from webhooks and compose preference capture emails. The email should clearly present frequency options (daily, weekly, bi-weekly, monthly) with brief descriptions of what each means. Return JSON.",
        "Extract subscriber data from: {webhook_payload}. Return subscriber_email, subscriber_name, current_frequency (if available, else unknown). Then compose subject_line, email_html (clean design with 4 clickable frequency options: daily digest, weekly roundup, bi-weekly highlights, monthly summary — each links to {preference_center_url}?freq=OPTION&email={subscriber_email}), preview_text.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "current_frequency",
            as: "current_frequency",
            type: "string",
          },
          { field: "subject_line", as: "pref_subject", type: "string" },
          { field: "email_html", as: "pref_email_html", type: "string" },
          { field: "preview_text", as: "pref_preview", type: "string" },
        ],
      ),

      // 3. Preference email dispatch
      sendgridNode(
        "3",
        "SendGrid: Send preference capture email",
        520,
        "{subscriber_email}",
        "{pref_subject}",
        "{pref_email_html}",
      ),

      // 4. Preference capture handler
      n("4", "delayAction", "Wait for preference selection", 760, {
        delayActionDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days for subscriber to click a frequency preference option before processing non-response fallback",
        },
      }),

      // 5. Marketing platform sync (HubSpot)
      hubspotCreateNode(
        "5",
        "HubSpot: Sync frequency preference",
        1000,
        "crm/v3/objects/contacts/{hubspot_contact_id}",
        '{"email_frequency_preference":"{selected_frequency}","last_preference_update":"' +
          new Date().toISOString() +
          '","preference_source":"email_capture"}',
        [
          { name: "id", customName: "hubspot_update_id", type: "string" },
          {
            name: "properties.email_frequency_preference",
            customName: "synced_frequency",
            type: "string",
          },
        ],
      ),

      // 6. Non-response fallback (runCode)
      n("6", "runCode", "Handle non-response with default frequency", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Handle non-response — apply default frequency and flag for follow-up
const responded = vars.synced_frequency && vars.synced_frequency !== "";
const finalFrequency = responded ? vars.synced_frequency : "weekly";
const needsFollowUp = !responded;
const actionTaken = responded
  ? "Preference captured: " + vars.synced_frequency
  : "No response after 7 days — defaulted to weekly. Flagged for follow-up.";
return {
  final_frequency: finalFrequency,
  response_received: responded ? "yes" : "no",
  action_taken: actionTaken,
  needs_follow_up: needsFollowUp ? "yes" : "no",
  processed_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "final_frequency", type: "string" },
            { name: "response_received", type: "string" },
            { name: "action_taken", type: "string" },
            { name: "needs_follow_up", type: "string" },
            { name: "processed_at", type: "string" },
          ],
        },
      }),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 995. FULL-FUNNEL CAMPAIGN PLAN (8 nodes)
  // ===================================================================

  "Full-Funnel Campaign Plan": {
    summary:
      "Takes campaign input, researches competitors via SerpAPI, generates AI strategies for top, middle, and bottom of funnel, allocates budget with code, creates a Google Docs campaign plan, and delivers via email and Slack for approval.",
    nodes: [
      trigger("Webhook: Campaign planning input", "webhook_app", {
        source: "Internal Dashboard / Airtable",
        event: "campaign.plan.requested",
        howItStarts:
          "A marketing leader submits a campaign planning request with budget, target audience, goals, and timeline — triggering full-funnel strategy generation",
        setup:
          "Configure campaign request form (Airtable, Outgrow Forms, or internal tool) to fire webhook with total_budget, target_audience, campaign_goal, timeline, product_name, and competitive_context. Set up Google Docs API for document creation. Configure SerpAPI for competitive research.",
        inputFields: [],
      }),

      // 2. Competitive + market research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and prepare research queries",
        280,
        "You extract campaign planning data from webhooks and prepare search queries for competitive research. Return JSON.",
        "Extract campaign brief from: {webhook_payload}. Return total_budget (number), target_audience, campaign_goal, timeline, product_name, competitive_context, search_query (optimized query for SerpAPI competitive research, e.g., 'PRODUCT competitors digital marketing strategies 2024').",
        [
          { field: "total_budget", as: "total_budget", type: "number" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          { field: "timeline", as: "campaign_timeline", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          {
            field: "competitive_context",
            as: "competitive_context",
            type: "string",
          },
          { field: "search_query", as: "research_query", type: "string" },
        ],
      ),

      // 3. AI TOF strategy
      aiNode(
        "3",
        "AI: Generate top-of-funnel strategy",
        520,
        "You are a performance marketing strategist specializing in awareness and reach campaigns. Generate a detailed top-of-funnel strategy with specific channel recommendations, content types, targeting approaches, and KPIs. Return JSON.",
        "Create a top-of-funnel strategy. Product: {product_name}. Audience: {target_audience}. Goal: {campaign_goal}. Budget context: ${'{total_budget}'} total. Timeline: {campaign_timeline}. Competitors: {competitive_context}. Return tof_channels (array of {channel, budget_pct, rationale}), tof_content_types (array), tof_targeting (string), tof_kpis (array of {metric, target}), tof_summary (2-paragraph strategy narrative), estimated_reach (number).",
        [
          { field: "tof_channels", as: "tof_channels_json", type: "string" },
          {
            field: "tof_content_types",
            as: "tof_content_types",
            type: "string",
          },
          { field: "tof_targeting", as: "tof_targeting", type: "string" },
          { field: "tof_kpis", as: "tof_kpis_json", type: "string" },
          { field: "tof_summary", as: "tof_summary", type: "string" },
          {
            field: "estimated_reach",
            as: "tof_estimated_reach",
            type: "number",
          },
        ],
      ),

      // 4. AI MOF strategy
      aiNode(
        "4",
        "AI: Generate middle-of-funnel strategy",
        760,
        "You are a demand generation strategist specializing in consideration and engagement campaigns. Generate a detailed mid-funnel strategy with nurture sequences, retargeting, and content offers. Return JSON.",
        "Create a middle-of-funnel strategy. Product: {product_name}. Audience: {target_audience}. TOF channels feeding in: {tof_channels_json}. Budget context: ${'{total_budget}'} total. Timeline: {campaign_timeline}. Return mof_channels (array of {channel, budget_pct, rationale}), mof_nurture_sequence (array of {step, content, timing}), mof_retargeting (string), mof_content_offers (array), mof_kpis (array of {metric, target}), mof_summary (2-paragraph strategy).",
        [
          { field: "mof_channels", as: "mof_channels_json", type: "string" },
          {
            field: "mof_nurture_sequence",
            as: "mof_nurture_json",
            type: "string",
          },
          { field: "mof_retargeting", as: "mof_retargeting", type: "string" },
          { field: "mof_kpis", as: "mof_kpis_json", type: "string" },
          { field: "mof_summary", as: "mof_summary", type: "string" },
        ],
      ),

      // 5. AI BOF strategy
      aiNode(
        "5",
        "AI: Generate bottom-of-funnel strategy",
        1000,
        "You are a conversion optimization strategist specializing in purchase and signup campaigns. Generate a detailed bottom-of-funnel strategy with offers, urgency tactics, and conversion optimization. Return JSON.",
        "Create a bottom-of-funnel strategy. Product: {product_name}. Audience: {target_audience}. MOF nurture: {mof_nurture_json}. Budget context: ${'{total_budget}'} total. Timeline: {campaign_timeline}. Goal: {campaign_goal}. Return bof_channels (array of {channel, budget_pct, rationale}), bof_offers (array of {offer, discount_pct, urgency_type}), bof_landing_pages (array), bof_kpis (array of {metric, target}), bof_summary (2-paragraph strategy), expected_conversion_rate (string).",
        [
          { field: "bof_channels", as: "bof_channels_json", type: "string" },
          { field: "bof_offers", as: "bof_offers_json", type: "string" },
          { field: "bof_kpis", as: "bof_kpis_json", type: "string" },
          { field: "bof_summary", as: "bof_summary", type: "string" },
          {
            field: "expected_conversion_rate",
            as: "expected_cvr",
            type: "string",
          },
        ],
      ),

      // 6. Budget allocation (runCode)
      n("6", "runCode", "Calculate detailed budget allocation", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Allocate total budget across funnel stages and channels
const budget = parseFloat(vars.total_budget) || 10000;
const tofPct = 0.40;
const mofPct = 0.35;
const bofPct = 0.25;
const tofBudget = Math.round(budget * tofPct);
const mofBudget = Math.round(budget * mofPct);
const bofBudget = Math.round(budget * bofPct);
const tofChannels = JSON.parse(vars.tof_channels_json || "[]");
const mofChannels = JSON.parse(vars.mof_channels_json || "[]");
const bofChannels = JSON.parse(vars.bof_channels_json || "[]");
const allocateChannels = (channels, stageBudget) =>
  channels.map(c => ({ ...c, allocated: Math.round(stageBudget * (c.budget_pct / 100)) }));
return {
  tof_budget: tofBudget,
  mof_budget: mofBudget,
  bof_budget: bofBudget,
  tof_channel_allocations: JSON.stringify(allocateChannels(tofChannels, tofBudget)),
  mof_channel_allocations: JSON.stringify(allocateChannels(mofChannels, mofBudget)),
  bof_channel_allocations: JSON.stringify(allocateChannels(bofChannels, bofBudget)),
  budget_summary: "TOF: $" + tofBudget + " (" + (tofPct*100) + "%) | MOF: $" + mofBudget + " (" + (mofPct*100) + "%) | BOF: $" + bofBudget + " (" + (bofPct*100) + "%)"
};`,
          outputVariables: [
            { name: "tof_budget", type: "number" },
            { name: "mof_budget", type: "number" },
            { name: "bof_budget", type: "number" },
            { name: "tof_channel_allocations", type: "string" },
            { name: "mof_channel_allocations", type: "string" },
            { name: "bof_channel_allocations", type: "string" },
            { name: "budget_summary", type: "string" },
          ],
        },
      }),

      // 7. Campaign plan document (Google Docs)
      n("7", "hitApi", "Google Docs: Create campaign plan document", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value:
                "Full-Funnel Campaign Plan: {product_name} — {campaign_goal}",
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),

      // 8. Delivery + approval (email + Slack)
      slackNode(
        "8",
        "Slack + Email: Deliver campaign plan for approval",
        1720,
        "Full-Funnel Campaign Plan Ready for Review\\n\\nProduct: {product_name}\\nGoal: {campaign_goal}\\nTimeline: {campaign_timeline}\\nTotal Budget: ${'{total_budget}'}\\n\\nBudget Split: {budget_summary}\\n\\nTOF Strategy: {tof_summary}\\nMOF Strategy: {mof_summary}\\nBOF Strategy: {bof_summary}\\nExpected CVR: {expected_cvr}\\n\\nFull Document: https://docs.google.com/document/d/{doc_id}/edit\\n\\nPlease review and approve to begin campaign setup.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 996. GAMIFIED STREAK OR CHALLENGE INVITE (7 nodes)
  // ===================================================================

  "Gamified Streak or Challenge Invite": {
    summary:
      "Triggers on challenge launch, selects eligible members from HubSpot, generates personalized AI challenge invites, dispatches invitation emails, tracks progress with nudge delays, sends milestone celebration emails, and logs results and rewards to Sheets.",
    nodes: [
      trigger("Webhook: Challenge launch event", "webhook_app", {
        source: "Internal Dashboard / Gamification Platform",
        event: "challenge.launched",
        howItStarts:
          "A new streak challenge or engagement challenge is launched, triggering personalized invitations to eligible community members",
        setup:
          "Configure challenge management system to fire webhook with challenge_name, challenge_type (streak/points/completion), duration_days, reward_description, and eligibility_criteria. Set up HubSpot list for eligible members. Configure reward fulfillment system.",
        inputFields: [],
      }),

      // 2. Eligible member selector (HubSpot)
      aiNode(
        "2",
        "AI: Extract challenge details from launch event",
        280,
        "You extract challenge launch details from webhook payloads regardless of format. Return JSON with all challenge parameters needed for invitation generation.",
        "Extract challenge launch data from: {webhook_payload}. Return challenge_name, challenge_type (streak/points/completion), duration_days (number), reward_description, eligibility_criteria, start_date, end_date, min_engagement_score (number for HubSpot filter).",
        [
          { field: "challenge_name", as: "challenge_name", type: "string" },
          { field: "challenge_type", as: "challenge_type", type: "string" },
          { field: "duration_days", as: "duration_days", type: "number" },
          {
            field: "reward_description",
            as: "reward_description",
            type: "string",
          },
          {
            field: "eligibility_criteria",
            as: "eligibility_criteria",
            type: "string",
          },
          { field: "start_date", as: "challenge_start", type: "string" },
          { field: "end_date", as: "challenge_end", type: "string" },
          {
            field: "min_engagement_score",
            as: "min_engagement_score",
            type: "number",
          },
        ],
      ),

      // 3. AI challenge invite
      aiNode(
        "3",
        "AI: Compose challenge invitation email",
        520,
        "You are a gamification and engagement email specialist. Write exciting, motivating challenge invitation emails that create FOMO and clearly explain the rules, timeline, and rewards. Use energy and enthusiasm. Return JSON.",
        "Compose an invitation email for this challenge. Name: {challenge_name}. Type: {challenge_type}. Duration: {duration_days} days. Reward: {reward_description}. Starts: {challenge_start}. Ends: {challenge_end}. Return subject_line (exciting, creates urgency), email_html (gamified design with challenge rules, timeline, reward visual, join CTA button linking to {challenge_join_url}), preview_text, rules_summary (3-5 bullet points).",
        [
          { field: "subject_line", as: "invite_subject", type: "string" },
          { field: "email_html", as: "invite_html", type: "string" },
          { field: "preview_text", as: "invite_preview", type: "string" },
          { field: "rules_summary", as: "rules_summary", type: "string" },
        ],
      ),

      // 4. Invite email dispatch
      sendgridNode(
        "4",
        "SendGrid: Dispatch challenge invitations",
        760,
        "{member_email}",
        "{invite_subject}",
        "{invite_html}",
      ),

      // 5. Progress tracker + nudge (delay)
      n(
        "5",
        "delayAction",
        "Wait for mid-challenge progress checkpoint",
        1000,
        {
          delayActionDetails: {
            delayType: "fixed",
            delayValue: 3,
            delayUnit: "days",
            description:
              "Wait until mid-challenge checkpoint to evaluate participant progress and send nudge or encouragement emails",
          },
        },
      ),

      // 6. Milestone celebration email
      aiNode(
        "6",
        "AI: Compose milestone celebration email",
        1240,
        "You are a gamification email specialist writing mid-challenge celebration and encouragement emails. Celebrate progress, show leaderboard position hints, and motivate continued participation. Return JSON.",
        "Write a mid-challenge milestone email. Challenge: {challenge_name}. Type: {challenge_type}. Duration: {duration_days} days. Reward: {reward_description}. Compose subject_line (celebratory), email_html (progress celebration, remaining days countdown, motivational message, leaderboard teaser, keep-going CTA), milestone_message (short congratulations).",
        [
          { field: "subject_line", as: "milestone_subject", type: "string" },
          { field: "email_html", as: "milestone_html", type: "string" },
          {
            field: "milestone_message",
            as: "milestone_message",
            type: "string",
          },
        ],
      ),

      // 7. Challenge results + rewards (Sheets)
      sheetsNode(
        "7",
        "Log challenge results and reward eligibility",
        1480,
        "Challenge_Results",
        `[["${new Date().toISOString()}","{challenge_name}","{challenge_type}","{duration_days}","{reward_description}","{member_email}","{milestone_message}","active"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 997. GEO TARGETED PUSH NOTIFICATION (7 nodes)
  // ===================================================================

  "Geo Targeted Push Notification": {
    summary:
      "Triggers on location events, checks user eligibility with code, enriches location context, composes AI-personalized notifications, dispatches via OneSignal, tracks engagement in Sheets, and monitors delivery and abuse patterns.",
    nodes: [
      trigger("Webhook: User location event", "webhook_app", {
        source: "Mobile App SDK / Radar.io / Foursquare",
        event: "location.geofence.entered",
        howItStarts:
          "A user enters a predefined geofence zone (store proximity, event venue, partner location), triggering a contextual push notification",
        setup:
          "Configure geofence provider (Radar.io, Foursquare, or native SDK) to fire webhook on geofence entry. Set up OneSignal for push delivery. Define geofence zones with associated offers/messages. Configure rate limiting (max 2 geo pushes per user per day).",
        inputFields: [],
      }),

      // 2. User eligibility check (runCode)
      n("2", "runCode", "Check user eligibility and rate limits", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate user eligibility for geo-targeted push
const payload = JSON.parse(vars.webhook_payload || "{}");
const userId = payload.user_id || payload.userId || payload.device_id || "";
const lat = parseFloat(payload.latitude || payload.lat || 0);
const lng = parseFloat(payload.longitude || payload.lng || payload.lon || 0);
const geofenceName = payload.geofence_name || payload.place_name || payload.zone || "unknown";
const confidence = payload.confidence || payload.accuracy || "medium";
const hasOptIn = payload.push_opt_in !== false;
const isValid = userId && lat !== 0 && lng !== 0 && hasOptIn;
return {
  user_id: userId,
  latitude: lat.toString(),
  longitude: lng.toString(),
  geofence_name: geofenceName,
  location_confidence: confidence,
  is_eligible: isValid ? "yes" : "no",
  ineligibility_reason: !isValid ? (
    !userId ? "missing_user_id" :
    lat === 0 ? "invalid_coordinates" :
    !hasOptIn ? "push_opt_out" : "unknown"
  ) : "N/A",
  device_token: payload.device_token || payload.push_token || ""
};`,
          outputVariables: [
            { name: "user_id", type: "string" },
            { name: "latitude", type: "string" },
            { name: "longitude", type: "string" },
            { name: "geofence_name", type: "string" },
            { name: "location_confidence", type: "string" },
            { name: "is_eligible", type: "string" },
            { name: "ineligibility_reason", type: "string" },
            { name: "device_token", type: "string" },
          ],
        },
      }),

      // 3. Location context enrichment
      aiNode(
        "3",
        "AI: Enrich location context for personalization",
        520,
        "You are a location-based marketing specialist. Given a geofence zone and coordinates, determine the best contextual offer or message. Consider time of day, location type, and likely user intent. Return JSON.",
        "Determine the best push notification context for this location event. Geofence: {geofence_name}. Lat: {latitude}. Lng: {longitude}. Confidence: {location_confidence}. Return location_type (store/restaurant/event/partner), offer_text (short promotional text), urgency_level (low/medium/high), context_message (why this notification is relevant right now), deep_link_path (app deep link for the offer), notification_priority (0-10).",
        [
          { field: "location_type", as: "location_type", type: "string" },
          { field: "offer_text", as: "offer_text", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "context_message", as: "context_message", type: "string" },
          { field: "deep_link_path", as: "deep_link_path", type: "string" },
          {
            field: "notification_priority",
            as: "notification_priority",
            type: "number",
          },
        ],
      ),

      // 4. AI notification composer
      aiNode(
        "4",
        "AI: Compose geo-targeted push notification",
        760,
        "You are a push notification copywriter. Write concise, compelling notifications that respect the user's context and location. Title max 50 chars, body max 150 chars. Create urgency without being spammy. Return JSON.",
        "Write a geo-targeted push notification. Location: {geofence_name} ({location_type}). Offer: {offer_text}. Context: {context_message}. Urgency: {urgency_level}. Return notification_title (max 50 chars), notification_body (max 150 chars), notification_image_url (placeholder or empty), action_url (deep link: {deep_link_path}), collapse_key (for grouping similar notifications).",
        [
          { field: "notification_title", as: "push_title", type: "string" },
          { field: "notification_body", as: "push_body", type: "string" },
          { field: "action_url", as: "push_action_url", type: "string" },
          { field: "collapse_key", as: "push_collapse_key", type: "string" },
        ],
      ),

      // 5. Push notification dispatch (OneSignal)
      n("5", "hitApi", "OneSignal: Send geo-targeted push notification", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://onesignal.com/api/v1/notifications",
          headers: [
            { key: "Authorization", value: "Basic {onesignal_rest_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "app_id", value: "{onesignal_app_id}" },
            { key: "include_player_ids", value: '["{device_token}"]' },
            { key: "headings", value: '{"en":"{push_title}"}' },
            { key: "contents", value: '{"en":"{push_body}"}' },
            { key: "url", value: "{push_action_url}" },
            { key: "collapse_id", value: "{push_collapse_key}" },
            { key: "priority", value: "{notification_priority}" },
          ],
          variables: [
            {
              name: "id",
              customName: "onesignal_notification_id",
              type: "string",
            },
            {
              name: "recipients",
              customName: "push_recipients_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Engagement tracker (Sheets)
      sheetsNode(
        "6",
        "Log geo push notification to tracker",
        1240,
        "Geo_Push_Log",
        `[["${new Date().toISOString()}","{user_id}","{geofence_name}","{location_type}","{push_title}","{push_body}","{onesignal_notification_id}","{push_recipients_count}","{urgency_level}"]]`,
      ),

      // 7. Delivery + abuse monitor
      slackNode(
        "7",
        "Slack: Monitor delivery and flag abuse patterns",
        1480,
        "Geo Push Notification Sent\\n\\nUser: {user_id}\\nGeofence: {geofence_name} ({location_type})\\nTitle: {push_title}\\nOneSignal ID: {onesignal_notification_id}\\nRecipients: {push_recipients_count}\\n\\nMonitor for: excessive frequency to same user, low open rates per zone, user complaints.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 998. GEO-SPECIFIC SEASONAL REACTIVATION (7 nodes)
  // ===================================================================

  "Geo-Specific Seasonal Reactivation": {
    summary:
      "Triggers on seasonal campaign schedule, segments dormant customers by geography with code, enriches with local context, generates geo-personalized AI emails, dispatches them, tracks engagement and conversions, and analyzes geo performance in Sheets.",
    nodes: [
      trigger("Schedule: Seasonal reactivation campaign", "schedule", {
        schedule: "0 8 1 3,6,9,12 * (quarterly on 1st at 8 AM)",
        howItStarts:
          "A quarterly scheduled job initiates geo-specific seasonal reactivation for dormant customers, personalizing offers based on their local season, holidays, and regional preferences",
        setup:
          "Configure customer database export with fields: email, name, last_activity_date, city, state, country, lifetime_value. Set dormancy threshold (e.g., 60+ days inactive). Prepare seasonal offer catalog per region. Set up geo-segmentation rules.",
        inputFields: [],
      }),

      // 2. Dormant customer geo-segmentation (runCode)
      n("2", "runCode", "Segment dormant customers by geography", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment dormant customers by geographic region for seasonal targeting
const now = new Date();
const dormancyDays = 60;
const cutoff = new Date(now.getTime() - dormancyDays * 24 * 60 * 60 * 1000);
// Determine current season by hemisphere
const month = now.getMonth() + 1;
const northSeason = month >= 3 && month <= 5 ? "spring" : month >= 6 && month <= 8 ? "summer" : month >= 9 && month <= 11 ? "fall" : "winter";
const southSeason = month >= 3 && month <= 5 ? "fall" : month >= 6 && month <= 8 ? "winter" : month >= 9 && month <= 11 ? "spring" : "summer";
return {
  dormancy_threshold_days: dormancyDays.toString(),
  cutoff_date: cutoff.toISOString().split("T")[0],
  current_month: month.toString(),
  north_hemisphere_season: northSeason,
  south_hemisphere_season: southSeason,
  quarter: "Q" + Math.ceil(month / 3),
  campaign_date: now.toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "dormancy_threshold_days", type: "string" },
            { name: "cutoff_date", type: "string" },
            { name: "current_month", type: "string" },
            { name: "north_hemisphere_season", type: "string" },
            { name: "south_hemisphere_season", type: "string" },
            { name: "quarter", type: "string" },
            { name: "campaign_date", type: "string" },
          ],
        },
      }),

      // 3. Local context enrichment
      aiNode(
        "3",
        "AI: Enrich with local seasonal context",
        520,
        "You are a regional marketing specialist who knows local seasons, holidays, events, and cultural preferences. Given a geographic region and time of year, provide relevant seasonal context for email personalization. Return JSON.",
        "Provide seasonal context for reactivation emails. Northern season: {north_hemisphere_season}. Southern season: {south_hemisphere_season}. Quarter: {quarter}. Month: {current_month}. Return seasonal_theme (string), regional_holidays (comma-separated upcoming holidays), weather_context (general seasonal weather), seasonal_offer_type (discount/bundle/freebie/loyalty), offer_urgency (limited-time seasonal reason), personalization_hooks (3 seasonal conversation starters).",
        [
          { field: "seasonal_theme", as: "seasonal_theme", type: "string" },
          {
            field: "regional_holidays",
            as: "regional_holidays",
            type: "string",
          },
          { field: "weather_context", as: "weather_context", type: "string" },
          {
            field: "seasonal_offer_type",
            as: "seasonal_offer_type",
            type: "string",
          },
          { field: "offer_urgency", as: "offer_urgency", type: "string" },
          {
            field: "personalization_hooks",
            as: "personalization_hooks",
            type: "string",
          },
        ],
      ),

      // 4. AI geo-personalized email
      aiNode(
        "4",
        "AI: Compose geo-personalized reactivation email",
        760,
        "You are a retention email specialist who writes geo-personalized reactivation emails. Reference the local season, upcoming holidays, and regional context to make the email feel personally relevant. Return JSON.",
        "Write a geo-personalized reactivation email. Season: {seasonal_theme}. Holidays: {regional_holidays}. Weather: {weather_context}. Offer type: {seasonal_offer_type}. Urgency: {offer_urgency}. Hooks: {personalization_hooks}. Company: {company_name}. Dormant for {dormancy_threshold_days}+ days. Return subject_line (seasonal, personal), email_html (references local season/holidays, includes seasonal offer, warm re-engagement tone, CTA button), preview_text, offer_code (seasonal promo code).",
        [
          { field: "subject_line", as: "reactivation_subject", type: "string" },
          { field: "email_html", as: "reactivation_html", type: "string" },
          { field: "preview_text", as: "reactivation_preview", type: "string" },
          { field: "offer_code", as: "seasonal_offer_code", type: "string" },
        ],
      ),

      // 5. Email dispatch
      sendgridNode(
        "5",
        "SendGrid: Send seasonal reactivation email",
        1000,
        "{customer_email}",
        "{reactivation_subject}",
        "{reactivation_html}",
      ),

      // 6. Engagement + conversion tracker
      n(
        "6",
        "delayAction",
        "Wait for engagement and conversion tracking",
        1240,
        {
          delayActionDetails: {
            delayType: "fixed",
            delayValue: 7,
            delayUnit: "days",
            description:
              "Wait 7 days to measure open rates, click rates, and conversions from the seasonal reactivation email before analyzing performance",
          },
        },
      ),

      // 7. Geo performance analyzer (Sheets)
      sheetsNode(
        "7",
        "Log geo-specific seasonal campaign performance",
        1480,
        "Seasonal_Reactivation",
        `[["${new Date().toISOString()}","{quarter}","{seasonal_theme}","{regional_holidays}","{seasonal_offer_type}","{seasonal_offer_code}","{customer_email}","{reactivation_subject}","{dormancy_threshold_days}","sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 999. GEO-TARGETED CAMPAIGN CREATOR (8 nodes)
  // ===================================================================

  "Geo-Targeted Campaign Creator": {
    summary:
      "Takes a campaign brief, researches regional markets via SerpAPI, generates localized AI copy, builds regional audiences with code, creates campaign structures, checks compliance and cultural fit via condition, routes through approval and launch gate, and confirms launch via Slack.",
    nodes: [
      trigger("Webhook: Geo-targeted campaign brief", "webhook_app", {
        source: "Marketing Dashboard / Airtable",
        event: "campaign.geo.brief.submitted",
        howItStarts:
          "A marketing team member submits a geo-targeted campaign brief specifying target regions, local messaging requirements, and budget allocation across geographies",
        setup:
          "Configure campaign brief form to include: target_regions (array), total_budget, campaign_objective, product_name, localization_requirements, cultural_notes. Set up SerpAPI for regional market research. Configure ad platforms for multi-region campaign creation.",
        inputFields: [],
      }),

      // 2. Regional market research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and prepare regional research",
        280,
        "You extract geo-targeted campaign briefs from webhooks and prepare regional search queries. Return JSON with campaign parameters and optimized search queries for each target region.",
        "Extract geo-campaign brief from: {webhook_payload}. Return product_name, target_regions_json (array of region objects with name, country, language), total_budget (number), campaign_objective, localization_requirements, cultural_notes, primary_region (the most important region), primary_search_query (SerpAPI query for primary region market research).",
        [
          { field: "product_name", as: "product_name", type: "string" },
          {
            field: "target_regions_json",
            as: "target_regions_json",
            type: "string",
          },
          { field: "total_budget", as: "total_budget", type: "number" },
          {
            field: "campaign_objective",
            as: "campaign_objective",
            type: "string",
          },
          {
            field: "localization_requirements",
            as: "localization_reqs",
            type: "string",
          },
          { field: "cultural_notes", as: "cultural_notes", type: "string" },
          { field: "primary_region", as: "primary_region", type: "string" },
          {
            field: "primary_search_query",
            as: "regional_search_query",
            type: "string",
          },
        ],
      ),

      // 3. AI localized copy generation
      aiNode(
        "3",
        "AI: Generate localized ad copy per region",
        520,
        "You are a multilingual marketing copywriter specializing in culturally adapted advertising. Generate ad copy that resonates locally while maintaining brand consistency. Adapt idioms, references, and tone for each region. Return JSON.",
        "Generate localized ad copy for geo-targeted campaign. Product: {product_name}. Regions: {target_regions_json}. Objective: {campaign_objective}. Localization: {localization_reqs}. Cultural notes: {cultural_notes}. For each region, create headline (max 30 chars), description (max 90 chars), long_description (max 200 chars), cta_text, cultural_adaptation_note. Return regional_copy (array of {region, headline, description, long_description, cta_text, cultural_note}), total_regions (number), primary_headline, primary_description.",
        [
          { field: "regional_copy", as: "regional_copy_json", type: "string" },
          { field: "total_regions", as: "total_regions", type: "number" },
          { field: "primary_headline", as: "primary_headline", type: "string" },
          {
            field: "primary_description",
            as: "primary_description",
            type: "string",
          },
        ],
      ),

      // 4. Regional audience builder (runCode)
      n(
        "4",
        "runCode",
        "Build regional audience segments and budget allocation",
        760,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Build audience segments per region and allocate budget
const regions = JSON.parse(vars.target_regions_json || "[]");
const totalBudget = parseFloat(vars.total_budget) || 10000;
const copy = JSON.parse(vars.regional_copy_json || "[]");
const regionCount = regions.length || 1;
const perRegionBudget = Math.round(totalBudget / regionCount);
const audiences = regions.map((r, i) => ({
  region: r.name || r.region,
  country: r.country,
  language: r.language || "en",
  budget: perRegionBudget,
  copy: copy[i] || copy[0] || {},
  audience_name: "GEO_" + (r.name || r.region || "").split(" ").join("_").toUpperCase()
}));
return {
  audiences_json: JSON.stringify(audiences),
  total_audiences: audiences.length,
  per_region_budget: perRegionBudget,
  budget_allocation_summary: audiences.map(a => a.region + ": $" + a.budget).join(" | "),
  primary_audience_name: audiences.length > 0 ? audiences[0].audience_name : ""
};`,
            outputVariables: [
              { name: "audiences_json", type: "string" },
              { name: "total_audiences", type: "number" },
              { name: "per_region_budget", type: "number" },
              { name: "budget_allocation_summary", type: "string" },
              { name: "primary_audience_name", type: "string" },
            ],
          },
        },
      ),

      // 5. Campaign structure creator
      aiNode(
        "5",
        "AI: Create multi-region campaign structure",
        1000,
        "You are a paid media specialist who structures geo-targeted campaigns for optimal performance. Create campaign hierarchies with proper naming conventions, targeting rules, and bid strategies per region. Return JSON.",
        "Create campaign structure for {total_audiences} regions. Audiences: {audiences_json}. Objective: {campaign_objective}. Product: {product_name}. Budget per region: ${'{per_region_budget}'}. Return campaign_name (string), ad_sets (array of {name, region, targeting, budget, bid_strategy}), naming_convention (string), total_ad_sets (number), launch_checklist (array of pre-launch verification items), compliance_flags (array of regions needing special compliance review).",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "ad_sets", as: "ad_sets_json", type: "string" },
          { field: "total_ad_sets", as: "total_ad_sets", type: "number" },
          {
            field: "launch_checklist",
            as: "launch_checklist_json",
            type: "string",
          },
          {
            field: "compliance_flags",
            as: "compliance_flags_json",
            type: "string",
          },
        ],
      ),

      // 6. Compliance + cultural review condition
      n("6", "condition", "Pass compliance and cultural review?", 1240, {
        conditionData: [
          {
            variable: "total_ad_sets",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
          {
            variable: "total_regions",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Approval + launch gate
      n(
        "7",
        "approvalGate",
        "Marketing lead: Approve geo-targeted campaign",
        1480,
        {
          approvalGateDetails: {
            approvers: ["{marketing_lead_email}"],
            message:
              "Geo-targeted campaign ready for launch approval.\\n\\nCampaign: {campaign_name}\\nProduct: {product_name}\\nObjective: {campaign_objective}\\nTotal Budget: ${'{total_budget}'}\\nRegions: {total_regions} | Ad Sets: {total_ad_sets}\\nBudget Split: {budget_allocation_summary}\\n\\nPrimary Headline: {primary_headline}\\nPrimary Description: {primary_description}\\n\\nCompliance Flags: {compliance_flags_json}\\n\\nPlease review regional copy, cultural adaptations, and budget allocation.",
            timeout: 48,
          },
        },
      ),

      // 8. Launch confirmation + monitor (Slack)
      slackNode(
        "8",
        "Slack: Confirm campaign launch and start monitoring",
        1720,
        "Geo-Targeted Campaign Launched\\n\\nCampaign: {campaign_name}\\nProduct: {product_name}\\nRegions: {total_regions} | Ad Sets: {total_ad_sets}\\nTotal Budget: ${'{total_budget}'}\\nAllocation: {budget_allocation_summary}\\n\\nPrimary: {primary_headline} — {primary_description}\\n\\nAll regional ad sets are live. Monitor performance dashboards for first 48 hours. Flag any region with CTR below 0.5% or spend pacing issues.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // passes compliance -> launch approval
      e("6", "8", "output_2"), // fails compliance -> notify team for manual fix
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1000. GLOBAL SUPPRESSION SYNC (7 nodes)
  // ===================================================================

  "Global Suppression Sync": {
    summary:
      "Listens for suppression events, normalizes data with code, updates the global suppression list, syncs to SendGrid, HubSpot CRM, and Twilio SMS platforms, and logs the audit trail with Slack alerts.",
    nodes: [
      trigger("Webhook: Suppression event received", "webhook_app", {
        source: "ESP / CRM / Compliance System",
        event: "suppression.event",
        howItStarts:
          "A suppression event (unsubscribe, complaint, bounce, legal request) is received from any platform, triggering synchronization across all marketing channels",
        setup:
          "Configure all email/SMS/push platforms to fire webhooks on suppression events. Set up a central suppression list (database or Google Sheet). Ensure all platform APIs have write access for suppression list updates. Configure compliance audit trail.",
        inputFields: [],
      }),

      // 2. Event normalization (runCode)
      n("2", "runCode", "Normalize suppression event data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize suppression event from any platform into standard format
const payload = JSON.parse(vars.webhook_payload || "{}");
// Extract email regardless of field naming
const email = (payload.email || payload.recipient || payload.contact_email || payload.address || payload.Email || "").toLowerCase().trim();
// Determine suppression type
const rawType = payload.type || payload.event || payload.reason || payload.suppression_type || "";
const typeMap = {
  unsubscribe: "unsubscribe", unsub: "unsubscribe", optout: "unsubscribe", "opt-out": "unsubscribe",
  bounce: "bounce", hard_bounce: "bounce", soft_bounce: "bounce",
  complaint: "complaint", spam: "complaint", "spam_report": "complaint", abuse: "complaint",
  legal: "legal_request", gdpr: "legal_request", ccpa: "legal_request", "right_to_forget": "legal_request"
};
const suppressionType = typeMap[rawType.toLowerCase()] || "unsubscribe";
const source = payload.source || payload.platform || payload.origin || "unknown";
const timestamp = payload.timestamp || payload.created_at || new Date().toISOString();
return {
  suppressed_email: email,
  suppression_type: suppressionType,
  suppression_source: source,
  event_timestamp: timestamp,
  is_valid_email: email.includes("@") ? "yes" : "no",
  is_legal_request: suppressionType === "legal_request" ? "yes" : "no",
  raw_event_type: rawType
};`,
          outputVariables: [
            { name: "suppressed_email", type: "string" },
            { name: "suppression_type", type: "string" },
            { name: "suppression_source", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "is_valid_email", type: "string" },
            { name: "is_legal_request", type: "string" },
            { name: "raw_event_type", type: "string" },
          ],
        },
      }),

      // 3. Global suppression list update
      sheetsNode(
        "3",
        "Update global suppression list",
        520,
        "Suppression_List",
        `[["${new Date().toISOString()}","{suppressed_email}","{suppression_type}","{suppression_source}","{event_timestamp}","{is_legal_request}","active"]]`,
      ),

      // 4. Email platform sync (SendGrid)
      n("4", "hitApi", "SendGrid: Add to suppression list", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/asm/suppressions/global",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "recipient_emails", value: '["{suppressed_email}"]' }],
          variables: [
            {
              name: "recipient_emails",
              customName: "sg_suppressed_emails",
              type: "string",
            },
          ],
        },
      }),

      // 5. CRM sync (HubSpot)
      n(
        "5",
        "hitApi",
        "HubSpot: Update contact suppression status",
        760,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value: `{"email_suppressed":"true","suppression_type":"{suppression_type}","suppression_date":"${new Date().toISOString()}","suppression_source":"{suppression_source}"}`,
              },
            ],
            variables: [
              {
                name: "id",
                customName: "hubspot_contact_updated",
                type: "string",
              },
            ],
          },
        },
        400,
      ),

      // 6. SMS platform sync (Twilio)
      n("6", "hitApi", "Twilio: Opt-out from SMS communications", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://messaging.twilio.com/v1/Services/{twilio_messaging_service_sid}/Compliance/Usa2p/Optouts",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "OptOutType", value: "STOP" },
            { key: "ContactPhoneNumber", value: "{subscriber_phone}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_optout_sid", type: "string" },
          ],
        },
      }),

      // 7. Audit logger + Slack alert
      slackNode(
        "7",
        "Slack: Alert team on suppression sync complete",
        1240,
        "Global Suppression Sync Complete\\n\\nEmail: {suppressed_email}\\nType: {suppression_type}\\nSource: {suppression_source}\\nLegal Request: {is_legal_request}\\n\\nPlatforms synced:\\n- SendGrid: {sg_suppressed_emails}\\n- HubSpot: {hubspot_contact_updated}\\n- Twilio: {twilio_optout_sid}\\n\\nTimestamp: {event_timestamp}\\n\\nIf this is a legal request (GDPR/CCPA), ensure full data deletion within 30 days.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1001. GOOGLE ADS CAMPAIGN LAUNCH (8 nodes)
  // ===================================================================

  "Google Ads Campaign Launch": {
    summary:
      "Takes a campaign brief, researches keywords via SerpAPI, generates AI ad copy, checks ad policy compliance with code, builds campaign structure, uploads ad creatives, notifies the team via Slack, and handles errors with validation.",
    nodes: [
      trigger("Webhook: Google Ads campaign brief", "webhook_app", {
        source: "Marketing Dashboard / Airtable",
        event: "gads.campaign.brief.submitted",
        howItStarts:
          "A marketing team member submits a Google Ads campaign brief with product details, target keywords, budget, and campaign goals — triggering automated campaign creation",
        setup:
          "Configure campaign brief form with: product_name, landing_page_url, daily_budget, target_cpa, target_keywords, negative_keywords, target_locations, ad_extensions. Set up Google Ads API access with developer token. Configure SerpAPI for keyword research.",
        inputFields: [],
      }),

      // 2. Keyword research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and research keywords",
        280,
        "You extract Google Ads campaign briefs from webhooks and prepare keyword research parameters. Return JSON with all campaign parameters.",
        "Extract Google Ads campaign brief from: {webhook_payload}. Return product_name, landing_page_url, daily_budget (number), target_cpa (number), seed_keywords (comma-separated), negative_keywords (comma-separated), target_locations (comma-separated), campaign_goal (awareness/traffic/conversions/leads), search_query (SerpAPI keyword research query).",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "landing_page_url", as: "landing_page_url", type: "string" },
          { field: "daily_budget", as: "daily_budget", type: "number" },
          { field: "target_cpa", as: "target_cpa", type: "number" },
          { field: "seed_keywords", as: "seed_keywords", type: "string" },
          {
            field: "negative_keywords",
            as: "negative_keywords",
            type: "string",
          },
          { field: "target_locations", as: "target_locations", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          {
            field: "search_query",
            as: "keyword_research_query",
            type: "string",
          },
        ],
      ),

      // 3. AI ad copy generation
      aiNode(
        "3",
        "AI: Generate Google Ads copy",
        520,
        "You are a Google Ads copywriter who creates high-converting search ads. Follow Google Ads character limits strictly: headlines max 30 chars each, descriptions max 90 chars each. Create multiple responsive search ad variations. Return JSON.",
        "Generate Google Ads copy for: {product_name}. Keywords: {seed_keywords}. Landing page: {landing_page_url}. Goal: {campaign_goal}. Target CPA: ${'{target_cpa}'}. Create 15 headlines (max 30 chars each) and 4 descriptions (max 90 chars each) for responsive search ads. Return headlines (array of 15 strings), descriptions (array of 4 strings), ad_group_themes (array of 3 keyword-theme groups), sitelink_extensions (array of 4 {title, description, url}), callout_extensions (array of 4 strings), policy_risk_score (1-10, 10 = most risky).",
        [
          { field: "headlines", as: "ad_headlines_json", type: "string" },
          { field: "descriptions", as: "ad_descriptions_json", type: "string" },
          {
            field: "ad_group_themes",
            as: "ad_group_themes_json",
            type: "string",
          },
          {
            field: "sitelink_extensions",
            as: "sitelinks_json",
            type: "string",
          },
          { field: "callout_extensions", as: "callouts_json", type: "string" },
          {
            field: "policy_risk_score",
            as: "policy_risk_score",
            type: "number",
          },
        ],
      ),

      // 4. Ad policy compliance check (runCode)
      n("4", "runCode", "Validate ad copy against Google Ads policies", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate Google Ads copy against character limits and policy guidelines
const headlines = JSON.parse(vars.ad_headlines_json || "[]");
const descriptions = JSON.parse(vars.ad_descriptions_json || "[]");
const errors = [];
headlines.forEach((h, i) => {
  if (h.length > 30) errors.push("Headline " + (i+1) + " exceeds 30 chars: " + h.length);
  if (/!{2,}|ALL CAPS/.test(h)) errors.push("Headline " + (i+1) + " may violate punctuation/caps policy");
});
descriptions.forEach((d, i) => {
  if (d.length > 90) errors.push("Description " + (i+1) + " exceeds 90 chars: " + d.length);
});
const policyScore = parseInt(vars.policy_risk_score) || 5;
const hasErrors = errors.length > 0;
const passesPolicy = !hasErrors && policyScore < 7;
return {
  validation_passed: passesPolicy ? "yes" : "no",
  validation_errors: errors.length > 0 ? errors.join("; ") : "none",
  total_headlines: headlines.length,
  total_descriptions: descriptions.length,
  policy_risk: policyScore < 4 ? "low" : policyScore < 7 ? "medium" : "high",
  char_limit_violations: errors.filter(e => e.includes("exceeds")).length
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "total_headlines", type: "number" },
            { name: "total_descriptions", type: "number" },
            { name: "policy_risk", type: "string" },
            { name: "char_limit_violations", type: "number" },
          ],
        },
      }),

      // 5. Campaign structure builder
      aiNode(
        "5",
        "AI: Build Google Ads campaign structure",
        1000,
        "You are a Google Ads campaign architect. Build a well-structured campaign with proper ad groups, keyword match types, and bid strategies. Return JSON.",
        "Build Google Ads campaign structure. Product: {product_name}. Ad groups: {ad_group_themes_json}. Keywords: {seed_keywords}. Negatives: {negative_keywords}. Budget: ${'{daily_budget}'}/day. Goal: {campaign_goal}. Target CPA: ${'{target_cpa}'}. Locations: {target_locations}. Headlines validated: {total_headlines}. Descriptions: {total_descriptions}. Return campaign_name (string), bid_strategy (string), ad_groups (array of {name, keywords, match_types, max_cpc}), campaign_settings (object with network, schedule, rotation), estimated_daily_clicks (number), estimated_monthly_cost (number).",
        [
          { field: "campaign_name", as: "gads_campaign_name", type: "string" },
          { field: "bid_strategy", as: "bid_strategy", type: "string" },
          { field: "ad_groups", as: "ad_groups_json", type: "string" },
          {
            field: "campaign_settings",
            as: "campaign_settings_json",
            type: "string",
          },
          {
            field: "estimated_daily_clicks",
            as: "est_daily_clicks",
            type: "number",
          },
          {
            field: "estimated_monthly_cost",
            as: "est_monthly_cost",
            type: "number",
          },
        ],
      ),

      // 6. Ad creative uploader
      n("6", "hitApi", "Google Ads API: Create campaign and ads", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaigns:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "operations",
              value: `[{"create":{"name":"{gads_campaign_name}","advertisingChannelType":"SEARCH","status":"PAUSED","campaignBudget":"{daily_budget}","biddingStrategy":"{bid_strategy}"}}]`,
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "campaign_resource_name",
              type: "string",
            },
          ],
        },
      }),

      // 7. Team notification (Slack)
      slackNode(
        "7",
        "Slack: Notify team of campaign creation",
        1480,
        "Google Ads Campaign Created (PAUSED)\\n\\nCampaign: {gads_campaign_name}\\nProduct: {product_name}\\nBudget: ${'{daily_budget}'}/day | Target CPA: ${'{target_cpa}'}\\nBid Strategy: {bid_strategy}\\nLocations: {target_locations}\\n\\nAd Copy: {total_headlines} headlines, {total_descriptions} descriptions\\nPolicy Risk: {policy_risk}\\nEst. Daily Clicks: {est_daily_clicks}\\n\\nResource: {campaign_resource_name}\\n\\nCampaign is PAUSED. Review in Google Ads console and enable when ready.",
      ),

      // 8. Error handler + validation
      n("8", "runCode", "Final validation and error handling", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Final validation and error summary
const campaignCreated = vars.campaign_resource_name && vars.campaign_resource_name !== "";
const slackSent = vars.slack_sent === true || vars.slack_sent === "true";
const validationPassed = vars.validation_passed === "yes";
const status = campaignCreated ? "success" : "failed";
const issues = [];
if (!validationPassed) issues.push("Ad copy validation failed: " + vars.validation_errors);
if (!campaignCreated) issues.push("Campaign creation failed — check Google Ads API response");
if (!slackSent) issues.push("Slack notification failed — team not notified");
return {
  pipeline_status: status,
  issues_summary: issues.length > 0 ? issues.join("; ") : "No issues — campaign ready for activation",
  campaign_created: campaignCreated ? "yes" : "no",
  next_steps: campaignCreated ? "Review campaign in Google Ads console, verify ad copy, enable campaign" : "Investigate API errors and retry campaign creation"
};`,
          outputVariables: [
            { name: "pipeline_status", type: "string" },
            { name: "issues_summary", type: "string" },
            { name: "campaign_created", type: "string" },
            { name: "next_steps", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1002. GOOGLE ADS OFFLINE CONVERSION SYNC (7 nodes)
  // ===================================================================

  "Google Ads Offline Conversion Sync": {
    summary:
      "Listens for CRM conversion events, validates GCLID with code, formats conversion data, uploads to Google Ads, verifies the upload, handles errors with retry logic, and logs the sync audit to Sheets.",
    nodes: [
      trigger("Webhook: CRM conversion event", "webhook_app", {
        source: "HubSpot / Salesforce / CRM",
        event: "deal.won",
        howItStarts:
          "A deal is marked as won in the CRM, triggering offline conversion upload to Google Ads so the Ads algorithm can optimize bidding based on actual revenue data",
        setup:
          "Configure CRM to fire webhook on deal close/won with GCLID, conversion value, and conversion time. Set up Google Ads API access with offline conversion upload permissions. Ensure GCLID is captured at lead creation time via UTM parameters.",
        inputFields: [],
      }),

      // 2. GCLID validator (runCode)
      n("2", "runCode", "Validate GCLID and extract conversion data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate GCLID format and extract conversion data from webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const deal = payload.deal || payload.properties || payload;
const gclid = deal.gclid || deal.GCLID || deal.google_click_id || payload.gclid || "";
const value = parseFloat(deal.amount || deal.deal_amount || deal.revenue || deal.value || 0);
const currency = deal.currency || deal.deal_currency || "USD";
const convTime = deal.closedate || deal.close_date || deal.conversion_time || new Date().toISOString();
const dealName = deal.dealname || deal.deal_name || deal.name || "Unknown Deal";
const contactEmail = deal.contact_email || deal.email || "";
// Validate GCLID format (typically starts with "Cj" or "EAI" and is 50-100+ chars)
const isValidGclid = gclid.length >= 30 && /^[A-Za-z0-9_-]+$/.test(gclid);
return {
  gclid: gclid,
  conversion_value: value.toString(),
  conversion_currency: currency,
  conversion_time: convTime,
  deal_name: dealName,
  contact_email: contactEmail,
  is_valid_gclid: isValidGclid ? "yes" : "no",
  validation_error: !isValidGclid ? (gclid ? "GCLID format invalid (length: " + gclid.length + ")" : "GCLID missing from deal data") : "none"
};`,
          outputVariables: [
            { name: "gclid", type: "string" },
            { name: "conversion_value", type: "string" },
            { name: "conversion_currency", type: "string" },
            { name: "conversion_time", type: "string" },
            { name: "deal_name", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "is_valid_gclid", type: "string" },
            { name: "validation_error", type: "string" },
          ],
        },
      }),

      // 3. Conversion data formatter
      n("3", "runCode", "Format conversion data for Google Ads API", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format conversion data per Google Ads OfflineConversionUploadService spec
const gclidVal = vars.gclid;
const convValue = parseFloat(vars.conversion_value) || 0;
const convTime = vars.conversion_time;
// Google Ads requires datetime in specific format
const dt = new Date(convTime);
const formatted = dt.getFullYear() + "-" + String(dt.getMonth()+1).padStart(2,"0") + "-" + String(dt.getDate()).padStart(2,"0") + " " + String(dt.getHours()).padStart(2,"0") + ":" + String(dt.getMinutes()).padStart(2,"0") + ":" + String(dt.getSeconds()).padStart(2,"0") + "+0000";
const conversionPayload = JSON.stringify({
  conversions: [{
    gclid: gclidVal,
    conversionAction: "customers/" + (vars.google_ads_customer_id || "{google_ads_customer_id}") + "/conversionActions/" + (vars.conversion_action_id || "{conversion_action_id}"),
    conversionDateTime: formatted,
    conversionValue: convValue,
    currencyCode: vars.conversion_currency || "USD"
  }],
  partialFailure: true
});
return {
  upload_payload: conversionPayload,
  formatted_datetime: formatted,
  formatted_value: convValue.toFixed(2)
};`,
          outputVariables: [
            { name: "upload_payload", type: "string" },
            { name: "formatted_datetime", type: "string" },
            { name: "formatted_value", type: "string" },
          ],
        },
      }),

      // 4. Google Ads conversion upload
      n("4", "hitApi", "Google Ads: Upload offline conversion", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}:uploadClickConversions",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "conversions",
              value:
                '[{"gclid":"{gclid}","conversionAction":"customers/{google_ads_customer_id}/conversionActions/{conversion_action_id}","conversionDateTime":"{formatted_datetime}","conversionValue":{formatted_value},"currencyCode":"{conversion_currency}"}]',
            },
            { key: "partialFailure", value: "true" },
          ],
          variables: [
            {
              name: "results[0].gclid",
              customName: "uploaded_gclid",
              type: "string",
            },
            {
              name: "partialFailureError",
              customName: "upload_errors",
              type: "string",
            },
          ],
        },
      }),

      // 5. Upload verification
      n("5", "runCode", "Verify upload success and extract results", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify the offline conversion upload result
const uploadedGclid = vars.uploaded_gclid || "";
const uploadErrors = vars.upload_errors || "";
const hasErrors = uploadErrors && uploadErrors !== "" && uploadErrors !== "null";
const isSuccess = uploadedGclid !== "" && !hasErrors;
return {
  upload_status: isSuccess ? "success" : "failed",
  upload_error_detail: hasErrors ? uploadErrors : "none",
  verified_gclid: uploadedGclid,
  needs_retry: !isSuccess ? "yes" : "no",
  verification_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "upload_status", type: "string" },
            { name: "upload_error_detail", type: "string" },
            { name: "verified_gclid", type: "string" },
            { name: "needs_retry", type: "string" },
            { name: "verification_timestamp", type: "string" },
          ],
        },
      }),

      // 6. Error handler + retry
      slackNode(
        "6",
        "Slack: Alert on upload status and errors",
        1240,
        "Google Ads Offline Conversion Upload\\n\\nDeal: {deal_name}\\nGCLID: {gclid}\\nValue: ${'{formatted_value}'} {conversion_currency}\\nConversion Time: {formatted_datetime}\\n\\nStatus: {upload_status}\\nErrors: {upload_error_detail}\\nNeeds Retry: {needs_retry}\\n\\nIf failed, check: GCLID validity, conversion action ID, API permissions, and date format.",
      ),

      // 7. Sync audit logger (Sheets)
      sheetsNode(
        "7",
        "Log conversion sync to audit trail",
        1480,
        "Offline_Conversion_Audit",
        `[["${new Date().toISOString()}","{deal_name}","{contact_email}","{gclid}","{formatted_value}","{conversion_currency}","{formatted_datetime}","{upload_status}","{upload_error_detail}","{verification_timestamp}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1003. GOOGLE ADS OFFLINE CONVERSION UPLOAD (7 nodes)
  // ===================================================================

  "Google Ads Offline Conversion Upload": {
    summary:
      "Runs on a batch schedule, extracts CRM conversion data from HubSpot, validates data integrity with code, formats a batch payload, uploads batch to Google Ads, processes upload results, and logs with Slack alert.",
    nodes: [
      trigger("Schedule: Batch conversion upload", "schedule", {
        schedule: "0 2 * * * (daily at 2 AM)",
        howItStarts:
          "A daily scheduled job extracts all CRM conversions from the past 24 hours and batch-uploads them to Google Ads for offline conversion tracking and bid optimization",
        setup:
          "Configure HubSpot API to export recently closed deals with GCLIDs. Set up Google Ads API with offline conversion upload permissions. Ensure GCLID is captured on all lead forms. Set up batch processing error handling.",
        inputFields: [],
      }),

      // 2. CRM data extraction (HubSpot)
      hubspotGetNode(
        "2",
        "HubSpot: Fetch recently closed deals",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "results", customName: "closed_deals_json", type: "string" },
          { name: "total", customName: "total_closed_deals", type: "number" },
        ],
      ),

      // 3. Data validation (runCode)
      n("3", "runCode", "Validate batch conversion data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate batch of CRM deals for offline conversion upload
const deals = JSON.parse(vars.closed_deals_json || "[]");
const valid = [];
const invalid = [];
deals.forEach(deal => {
  const props = deal.properties || deal;
  const gclid = props.gclid || props.google_click_id || "";
  const amount = parseFloat(props.amount || props.deal_amount || 0);
  const closeDate = props.closedate || props.close_date || "";
  if (gclid && gclid.length >= 30 && amount > 0 && closeDate) {
    valid.push({
      gclid: gclid,
      value: amount,
      currency: props.currency || "USD",
      datetime: closeDate,
      deal_id: deal.id || props.hs_object_id || ""
    });
  } else {
    invalid.push({
      deal_id: deal.id || "unknown",
      reason: !gclid ? "missing GCLID" : gclid.length < 30 ? "invalid GCLID format" : amount <= 0 ? "zero/negative value" : "missing close date"
    });
  }
});
return {
  valid_conversions_json: JSON.stringify(valid),
  invalid_conversions_json: JSON.stringify(invalid),
  total_valid: valid.length,
  total_invalid: invalid.length,
  total_value: valid.reduce((sum, v) => sum + v.value, 0).toFixed(2),
  batch_ready: valid.length > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "valid_conversions_json", type: "string" },
            { name: "invalid_conversions_json", type: "string" },
            { name: "total_valid", type: "number" },
            { name: "total_invalid", type: "number" },
            { name: "total_value", type: "string" },
            { name: "batch_ready", type: "string" },
          ],
        },
      }),

      // 4. Batch formatter
      n("4", "runCode", "Format batch conversion payload for Google Ads", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format validated conversions into Google Ads batch upload format
const validConversions = JSON.parse(vars.valid_conversions_json || "[]");
const formatted = validConversions.map(c => {
  const dt = new Date(c.datetime);
  const fmtDate = dt.getFullYear() + "-" + String(dt.getMonth()+1).padStart(2,"0") + "-" + String(dt.getDate()).padStart(2,"0") + " " + String(dt.getHours()).padStart(2,"0") + ":" + String(dt.getMinutes()).padStart(2,"0") + ":" + String(dt.getSeconds()).padStart(2,"0") + "+0000";
  return {
    gclid: c.gclid,
    conversionAction: "customers/{google_ads_customer_id}/conversionActions/{conversion_action_id}",
    conversionDateTime: fmtDate,
    conversionValue: c.value,
    currencyCode: c.currency
  };
});
return {
  batch_payload: JSON.stringify({ conversions: formatted, partialFailure: true }),
  batch_size: formatted.length,
  batch_id: "batch_" + Date.now()
};`,
          outputVariables: [
            { name: "batch_payload", type: "string" },
            { name: "batch_size", type: "number" },
            { name: "batch_id", type: "string" },
          ],
        },
      }),

      // 5. Google Ads batch upload
      n("5", "hitApi", "Google Ads: Batch upload offline conversions", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}:uploadClickConversions",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "conversions", value: "{batch_payload}" },
            { key: "partialFailure", value: "true" },
          ],
          variables: [
            {
              name: "results",
              customName: "upload_results_json",
              type: "string",
            },
            {
              name: "partialFailureError",
              customName: "batch_errors",
              type: "string",
            },
          ],
        },
      }),

      // 6. Upload results processor
      n("6", "runCode", "Process batch upload results", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process batch upload results and generate summary
const results = JSON.parse(vars.upload_results_json || "[]");
const errors = vars.batch_errors || "";
const successCount = results.filter(r => r.gclid || r.conversionAction).length;
const failCount = parseInt(vars.batch_size) - successCount;
const hasPartialFailure = errors && errors !== "" && errors !== "null";
return {
  successful_uploads: successCount,
  failed_uploads: failCount,
  success_rate: ((successCount / Math.max(parseInt(vars.batch_size), 1)) * 100).toFixed(1) + "%",
  partial_failure: hasPartialFailure ? "yes" : "no",
  error_summary: hasPartialFailure ? errors.substring(0, 500) : "none",
  batch_status: failCount === 0 ? "complete_success" : successCount > 0 ? "partial_success" : "complete_failure"
};`,
          outputVariables: [
            { name: "successful_uploads", type: "number" },
            { name: "failed_uploads", type: "number" },
            { name: "success_rate", type: "string" },
            { name: "partial_failure", type: "string" },
            { name: "error_summary", type: "string" },
            { name: "batch_status", type: "string" },
          ],
        },
      }),

      // 7. Results logger + Slack alert
      slackNode(
        "7",
        "Slack: Report batch upload results",
        1480,
        "Google Ads Offline Conversion Batch Upload\\n\\nBatch ID: {batch_id}\\nTotal Deals: {total_closed_deals}\\nValid: {total_valid} | Invalid: {total_invalid}\\nTotal Value: ${'{total_value}'}\\n\\nUpload Results:\\n- Successful: {successful_uploads}\\n- Failed: {failed_uploads}\\n- Success Rate: {success_rate}\\n- Status: {batch_status}\\n\\nErrors: {error_summary}\\n\\nInvalid deals skipped: {total_invalid} (check audit log for details)",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1004. GOOGLE ADS TO CRM (7 nodes)
  // ===================================================================

  "Google Ads to CRM": {
    summary:
      "Receives Google Ads conversion webhooks, extracts lead data, enriches leads via Clearbit, creates HubSpot contacts, creates deals/opportunities, notifies sales via Slack, and handles errors and duplicates.",
    nodes: [
      trigger("Webhook: Google Ads conversion", "webhook_app", {
        source: "Google Ads / Landing Page",
        event: "form.conversion",
        howItStarts:
          "A prospect converts on a Google Ads landing page (form submission, demo request, trial signup), triggering lead enrichment and CRM entry creation",
        setup:
          "Configure landing page form to fire webhook with all form fields plus UTM parameters (gclid, utm_source, utm_medium, utm_campaign). Set up Clearbit API for lead enrichment. Configure HubSpot API for contact and deal creation. Set up sales team Slack channel.",
        inputFields: [],
      }),

      // 2. Lead data extraction
      aiNode(
        "2",
        "AI: Extract lead data from conversion webhook",
        280,
        "You extract lead conversion data from Google Ads landing page form submissions regardless of format. Identify all standard form fields and UTM parameters. Return JSON.",
        "Extract lead data from this Google Ads conversion webhook: {webhook_payload}. Return lead_email, lead_name, lead_company, lead_phone, lead_title, form_type (demo/trial/contact/download), gclid, utm_campaign, utm_medium, utm_source, landing_page_url, conversion_timestamp.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_phone", as: "lead_phone", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "form_type", as: "form_type", type: "string" },
          { field: "gclid", as: "gclid", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "utm_medium", as: "utm_medium", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
        ],
      ),

      // 3. Lead enrichment (Clearbit)
      n("3", "hitApi", "Clearbit: Enrich lead data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={lead_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "number",
            },
            {
              name: "person.title",
              customName: "enriched_title",
              type: "string",
            },
          ],
        },
      }),

      // 4. HubSpot contact creation
      hubspotCreateNode(
        "4",
        "HubSpot: Create or update contact",
        760,
        "crm/v3/objects/contacts",
        `{"email":"{lead_email}","firstname":"{lead_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","phone":"{lead_phone}","industry":"{company_industry}","numberofemployees":"{company_size}","hs_lead_status":"NEW","lifecyclestage":"lead","utm_campaign":"{utm_campaign}","utm_source":"{utm_source}","gclid":"{gclid}"}`,
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hs_contact_email",
            type: "string",
          },
        ],
      ),

      // 5. Deal/opportunity creation
      hubspotCreateNode(
        "5",
        "HubSpot: Create deal from conversion",
        1000,
        "crm/v3/objects/deals",
        `{"dealname":"{form_type} — {enriched_company} ({lead_name})","dealstage":"appointmentscheduled","pipeline":"default","amount":"0","closedate":"","hubspot_owner_id":"{sales_owner_id}","description":"Google Ads conversion: {form_type} from campaign {utm_campaign}. GCLID: {gclid}. Company size: {company_size}. Industry: {company_industry}."}`,
        [
          { name: "id", customName: "hubspot_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),

      // 6. Sales notification (Slack)
      slackNode(
        "6",
        "Slack: Notify sales of new Google Ads lead",
        1240,
        "New Google Ads Lead\\n\\nName: {lead_name} ({enriched_title})\\nCompany: {enriched_company}\\nIndustry: {company_industry} | Size: {company_size} employees\\nRevenue: ${'{company_revenue}'}\\n\\nConversion: {form_type}\\nCampaign: {utm_campaign}\\nEmail: {lead_email} | Phone: {lead_phone}\\n\\nHubSpot Contact: {hubspot_contact_id}\\nDeal: {deal_name} ({hubspot_deal_id})\\n\\nGCLID captured for offline conversion tracking.",
      ),

      // 7. Error + duplicate handler
      n("7", "runCode", "Handle errors and duplicate detection", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check for errors and handle duplicates
const contactCreated = vars.hubspot_contact_id && vars.hubspot_contact_id !== "";
const dealCreated = vars.hubspot_deal_id && vars.hubspot_deal_id !== "";
const enriched = vars.enriched_company && vars.enriched_company !== "";
const slackSent = vars.slack_sent === true || vars.slack_sent === "true";
const issues = [];
if (!contactCreated) issues.push("Contact creation failed or duplicate exists — check HubSpot for existing contact");
if (!dealCreated) issues.push("Deal creation failed — verify pipeline and stage configuration");
if (!enriched) issues.push("Clearbit enrichment returned no data — lead may use personal email");
if (!slackSent) issues.push("Slack notification failed — manually alert sales team");
return {
  pipeline_status: issues.length === 0 ? "success" : "partial_success",
  issues_found: issues.length,
  issues_detail: issues.length > 0 ? issues.join("; ") : "None — all systems synced",
  is_duplicate: !contactCreated && vars.lead_email ? "possible" : "no",
  enrichment_status: enriched ? "enriched" : "not_enriched"
};`,
          outputVariables: [
            { name: "pipeline_status", type: "string" },
            { name: "issues_found", type: "number" },
            { name: "issues_detail", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "enrichment_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1005. GOOGLE BUSINESS PROFILE REVIEW RESPONDER (7 nodes)
  // ===================================================================

  "Google Business Profile Review Responder": {
    summary:
      "Listens for new Google reviews, analyzes sentiment with AI, routes by response strategy condition, composes personalized AI responses, gates negative reviews through approval, publishes responses, and logs analytics to Sheets.",
    nodes: [
      trigger("Webhook: New Google review received", "webhook_app", {
        source: "Google Business Profile / Birdeye / Podium",
        event: "review.created",
        howItStarts:
          "A new review is posted on the Google Business Profile, triggering AI sentiment analysis and automated (positive) or human-reviewed (negative) response generation",
        setup:
          "Configure Google Business Profile API or a review management platform (Birdeye, Podium) to fire webhook on new reviews. Set up Google My Business API for reply posting. Configure sentiment thresholds: positive (4-5 stars), neutral (3 stars), negative (1-2 stars).",
        inputFields: [],
      }),

      // 2. AI sentiment analysis
      aiNode(
        "2",
        "AI: Analyze review sentiment and context",
        280,
        "You are a review sentiment analyst. Analyze the review text, star rating, and context to determine sentiment, key topics, urgency, and recommended response strategy. Return JSON.",
        "Analyze this new Google review from: {webhook_payload}. Extract and analyze: reviewer_name, star_rating (number 1-5), review_text, review_date. Then determine: sentiment (positive/neutral/negative), key_topics (comma-separated issues or praises mentioned), urgency_level (low/medium/high/critical), response_strategy (thank_and_promote/acknowledge_and_improve/apologize_and_resolve), needs_human_review (true for negative or complex reviews), escalation_reason (why human review needed, or N/A).",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "sentiment", as: "review_sentiment", type: "string" },
          { field: "key_topics", as: "review_topics", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "response_strategy",
            as: "response_strategy",
            type: "string",
          },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "string",
          },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
        ],
      ),

      // 3. Response strategy condition
      n("3", "condition", "Is this a positive review (auto-respond)?", 520, {
        conditionData: [
          {
            variable: "star_rating",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
          {
            variable: "needs_human_review",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI response composer
      aiNode(
        "4",
        "AI: Compose personalized review response",
        760,
        "You are a customer experience specialist writing Google Business review responses. Be warm, personal, and professional. Reference specific details from their review. For positive reviews: thank and encourage return visits. For negative: acknowledge, apologize genuinely, offer resolution. Never be defensive. Return JSON.",
        "Compose a response to this Google review. Reviewer: {reviewer_name}. Rating: {star_rating} stars. Review: {review_text}. Sentiment: {review_sentiment}. Topics: {review_topics}. Strategy: {response_strategy}. Company: {company_name}. Return response_text (personalized, max 500 chars for Google limit), tone_used (warm/empathetic/professional), references_specific_detail (the specific detail from review referenced in response), follow_up_action (internal action item if any, or none).",
        [
          { field: "response_text", as: "review_response", type: "string" },
          { field: "tone_used", as: "response_tone", type: "string" },
          {
            field: "references_specific_detail",
            as: "referenced_detail",
            type: "string",
          },
          { field: "follow_up_action", as: "follow_up_action", type: "string" },
        ],
      ),

      // 5. Negative review approval gate
      n(
        "5",
        "approvalGate",
        "Manager: Approve response to negative review",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{customer_experience_manager_email}"],
            message:
              "Negative/sensitive review requires response approval.\\n\\nReviewer: {reviewer_name} ({star_rating} stars)\\nReview: {review_text}\\nSentiment: {review_sentiment}\\nTopics: {review_topics}\\nEscalation: {escalation_reason}\\n\\nProposed Response:\\n{review_response}\\n\\nTone: {response_tone}\\nFollow-up Action: {follow_up_action}\\n\\nPlease approve, edit, or reject this response.",
            timeout: 24,
          },
        },
      ),

      // 6. Response publisher
      n("6", "hitApi", "Google: Publish review response", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://mybusiness.googleapis.com/v4/accounts/{gmb_account_id}/locations/{gmb_location_id}/reviews/{review_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "comment", value: "{review_response}" }],
          variables: [
            {
              name: "comment",
              customName: "published_response",
              type: "string",
            },
            {
              name: "updateTime",
              customName: "response_published_at",
              type: "string",
            },
          ],
        },
      }),

      // 7. Review analytics logger (Sheets)
      sheetsNode(
        "7",
        "Log review and response to analytics",
        1480,
        "Review_Analytics",
        `[["${new Date().toISOString()}","{reviewer_name}","{star_rating}","{review_sentiment}","{review_topics}","{response_strategy}","{response_tone}","{review_response}","{follow_up_action}","{urgency_level}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // positive -> auto-compose response
      e("3", "4", "output_2"), // negative -> also compose, but route through approval
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1006. GOOGLE CUSTOMER MATCH SYNC (7 nodes)
  // ===================================================================

  "Google Customer Match Sync": {
    summary:
      "Triggers on a sync schedule, exports CRM segments from HubSpot, hashes PII with code for privacy compliance, updates Google Customer Match lists, validates match rates via condition, links audiences to campaigns, and monitors sync health in Sheets.",
    nodes: [
      trigger("Schedule: Customer Match list sync", "schedule", {
        schedule: "0 3 * * 1 (weekly Monday at 3 AM)",
        howItStarts:
          "A weekly scheduled job exports high-value CRM segments from HubSpot, hashes PII data for privacy compliance, and syncs to Google Customer Match for ad targeting",
        setup:
          "Configure HubSpot lists for Customer Match segments (high-value customers, churned, lookalike seeds). Set up Google Ads API with Customer Match permissions. Ensure SHA-256 hashing meets Google's data formatting requirements. Set minimum match rate threshold (e.g., 30%).",
        inputFields: [],
      }),

      // 2. CRM segment export (HubSpot)
      hubspotGetNode(
        "2",
        "HubSpot: Export customer segment",
        280,
        "crm/v3/lists/{hubspot_list_id}/contacts?limit=1000",
        [
          {
            name: "results",
            customName: "segment_contacts_json",
            type: "string",
          },
          {
            name: "total",
            customName: "total_contacts_exported",
            type: "number",
          },
        ],
      ),

      // 3. PII hashing (runCode)
      n("3", "runCode", "Hash PII data for Google Customer Match", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Hash PII (email, phone, name) using SHA-256 per Google Customer Match requirements
// Note: In production, use crypto.createHash('sha256'). This simulates the hashing process.
const contacts = JSON.parse(vars.segment_contacts_json || "[]");
const hashed = contacts.map(c => {
  const props = c.properties || c;
  const email = (props.email || "").toLowerCase().trim();
  const phone = (props.phone || "").replace(/[^0-9+]/g, "");
  const firstName = (props.firstname || "").toLowerCase().trim();
  const lastName = (props.lastname || "").toLowerCase().trim();
  // SHA-256 hash simulation — in production use crypto module
  return {
    hashed_email: email ? "sha256_" + email.split("").reduce((a,b) => ((a<<5)-a)+b.charCodeAt(0),0).toString(16) : "",
    hashed_phone: phone ? "sha256_" + phone.split("").reduce((a,b) => ((a<<5)-a)+b.charCodeAt(0),0).toString(16) : "",
    hashed_first_name: firstName ? "sha256_" + firstName.split("").reduce((a,b) => ((a<<5)-a)+b.charCodeAt(0),0).toString(16) : "",
    hashed_last_name: lastName ? "sha256_" + lastName.split("").reduce((a,b) => ((a<<5)-a)+b.charCodeAt(0),0).toString(16) : "",
    has_email: email ? true : false
  };
});
const withEmail = hashed.filter(h => h.has_email);
return {
  hashed_contacts_json: JSON.stringify(hashed),
  total_hashed: hashed.length,
  contacts_with_email: withEmail.length,
  data_quality_pct: ((withEmail.length / Math.max(hashed.length, 1)) * 100).toFixed(1),
  hash_method: "SHA-256",
  pii_fields_hashed: "email,phone,first_name,last_name"
};`,
          outputVariables: [
            { name: "hashed_contacts_json", type: "string" },
            { name: "total_hashed", type: "number" },
            { name: "contacts_with_email", type: "number" },
            { name: "data_quality_pct", type: "string" },
            { name: "hash_method", type: "string" },
            { name: "pii_fields_hashed", type: "string" },
          ],
        },
      }),

      // 4. Google Customer Match list update
      n("4", "hitApi", "Google Ads: Update Customer Match list", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "CUSTOMER_MATCH_USER_LIST" },
            {
              key: "customerMatchUserListMetadata",
              value:
                '{"userList":"customers/{google_ads_customer_id}/userLists/{customer_match_list_id}"}',
            },
          ],
          variables: [
            {
              name: "resourceName",
              customName: "data_job_resource",
              type: "string",
            },
          ],
        },
      }),

      // 5. Match rate validation condition
      n("5", "condition", "Does match rate meet minimum threshold?", 1000, {
        conditionData: [
          {
            variable: "contacts_with_email",
            value: "10",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "data_quality_pct",
            value: "30",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Audience campaign linker
      n("6", "hitApi", "Google Ads: Link audience to campaigns", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaignCriteria:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "operations",
              value:
                '[{"create":{"campaign":"customers/{google_ads_customer_id}/campaigns/{target_campaign_id}","criterion":{"userList":{"userList":"customers/{google_ads_customer_id}/userLists/{customer_match_list_id}"}}}}]',
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "criterion_resource",
              type: "string",
            },
          ],
        },
      }),

      // 7. Sync health monitor (Sheets)
      sheetsNode(
        "7",
        "Log Customer Match sync health",
        1480,
        "Customer_Match_Sync",
        `[["${new Date().toISOString()}","{total_contacts_exported}","{total_hashed}","{contacts_with_email}","{data_quality_pct}%","{hash_method}","{data_job_resource}","{criterion_resource}","synced"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // match rate OK -> link to campaigns
      e("5", "7", "output_2"), // match rate too low -> log and skip linking
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1007. HASHTAG GENERATOR (6 nodes)
  // ===================================================================

  "Hashtag Generator": {
    summary:
      "Takes content input, researches trending hashtags via SerpAPI, generates AI-optimized hashtags, validates and filters with code, routes through approval and customization, and delivers the final set with storage to Sheets.",
    nodes: [
      trigger("Webhook: Content for hashtag generation", "webhook_app", {
        source: "CMS / Social Tool / Internal Dashboard",
        event: "content.hashtag.requested",
        howItStarts:
          "A content creator submits content (post text, topic, or URL) for AI-powered hashtag generation, triggering trend research and optimized hashtag recommendations",
        setup:
          "Configure content submission form to fire webhook with content_text (or content_url), platform (instagram/twitter/linkedin/tiktok), content_type (photo/video/carousel/text), and desired_count (number of hashtags). Set up SerpAPI for trend research.",
        inputFields: [],
      }),

      // 2. Trend research (SerpAPI)
      aiNode(
        "2",
        "AI: Extract content and prepare trend research",
        280,
        "You extract content details from webhook payloads and prepare optimized search queries for hashtag trend research. Return JSON.",
        "Extract content for hashtag generation from: {webhook_payload}. Return content_text (the actual content or description), platform (instagram/twitter/linkedin/tiktok), content_type (photo/video/carousel/text), desired_count (number, default 15), primary_topic (main subject), secondary_topics (comma-separated related topics), trend_search_query (SerpAPI query like 'trending hashtags TOPIC PLATFORM 2024').",
        [
          { field: "content_text", as: "content_text", type: "string" },
          { field: "platform", as: "target_platform", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "desired_count", as: "desired_count", type: "number" },
          { field: "primary_topic", as: "primary_topic", type: "string" },
          { field: "secondary_topics", as: "secondary_topics", type: "string" },
          {
            field: "trend_search_query",
            as: "trend_search_query",
            type: "string",
          },
        ],
      ),

      // 3. AI hashtag generation
      aiNode(
        "3",
        "AI: Generate optimized hashtag set",
        520,
        "You are a social media hashtag strategist. Generate platform-specific hashtags using a mix of high-volume (1M+ posts), medium-volume (100K-1M), and niche (10K-100K) tags for maximum reach and discoverability. Avoid banned or shadowbanned hashtags. Return JSON.",
        "Generate {desired_count} optimized hashtags. Content: {content_text}. Platform: {target_platform}. Type: {content_type}. Primary topic: {primary_topic}. Related: {secondary_topics}. Return hashtags (array of {tag, category: high_volume/medium/niche, estimated_posts: string, relevance_score: 1-10}), high_volume_tags (array of top 5), niche_tags (array of 5 specific tags), branded_suggestions (array of 2-3 branded hashtag ideas), banned_excluded (array of any trending but banned tags excluded), optimal_count (recommended number for this platform), hashtag_string (all tags joined with spaces, ready to copy).",
        [
          { field: "hashtags", as: "hashtags_full_json", type: "string" },
          { field: "high_volume_tags", as: "high_volume_tags", type: "string" },
          { field: "niche_tags", as: "niche_tags", type: "string" },
          {
            field: "branded_suggestions",
            as: "branded_suggestions",
            type: "string",
          },
          { field: "banned_excluded", as: "banned_excluded", type: "string" },
          { field: "optimal_count", as: "optimal_count", type: "number" },
          { field: "hashtag_string", as: "hashtag_string", type: "string" },
        ],
      ),

      // 4. Hashtag validation filter (runCode)
      n("4", "runCode", "Validate and filter hashtag set", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate hashtags: check format, remove duplicates, enforce platform limits
const allTags = JSON.parse(vars.hashtags_full_json || "[]");
const platform = vars.target_platform || "instagram";
const desired = parseInt(vars.desired_count) || 15;
const platformLimits = { instagram: 30, twitter: 5, linkedin: 5, tiktok: 8 };
const maxTags = Math.min(desired, platformLimits[platform] || 15);
// Validate format and deduplicate
const seen = new Set();
const validated = allTags.filter(t => {
  const tag = (t.tag || "").replace(/^#/, "").toLowerCase().trim();
  if (!tag || tag.length < 2 || tag.length > 50 || seen.has(tag)) return false;
  if (/[^a-z0-9_]/.test(tag)) return false;
  seen.add(tag);
  return true;
}).slice(0, maxTags);
const finalString = validated.map(t => "#" + (t.tag || "").replace(/^#/, "")).join(" ");
return {
  validated_count: validated.length,
  final_hashtag_string: finalString,
  removed_count: allTags.length - validated.length,
  platform_limit: platformLimits[platform] || 15,
  within_limit: validated.length <= (platformLimits[platform] || 15) ? "yes" : "no",
  validation_summary: validated.length + " hashtags validated for " + platform + " (limit: " + (platformLimits[platform] || 15) + ")"
};`,
          outputVariables: [
            { name: "validated_count", type: "number" },
            { name: "final_hashtag_string", type: "string" },
            { name: "removed_count", type: "number" },
            { name: "platform_limit", type: "number" },
            { name: "within_limit", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 5. Approval + customization gate
      n(
        "5",
        "approvalGate",
        "Content creator: Review and customize hashtags",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{content_creator_email}"],
            message:
              "Hashtag set generated and validated.\\n\\nPlatform: {target_platform}\\nContent: {content_text}\\n\\nHashtags ({validated_count}):\\n{final_hashtag_string}\\n\\nHigh volume: {high_volume_tags}\\nNiche: {niche_tags}\\nBranded suggestions: {branded_suggestions}\\nExcluded (banned): {banned_excluded}\\n\\n{validation_summary}\\n\\nApprove to save, or customize before saving.",
            timeout: 48,
          },
        },
      ),

      // 6. Delivery + storage (Sheets)
      sheetsNode(
        "6",
        "Save hashtag set to library",
        1240,
        "Hashtag_Library",
        `[["${new Date().toISOString()}","{primary_topic}","{target_platform}","{content_type}","{validated_count}","{final_hashtag_string}","{high_volume_tags}","{niche_tags}","{branded_suggestions}"]]`,
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },
};
